"use client";

import { c as _c } from "react/compiler-runtime";
import { SquareArrowOutUpRight } from "lucide-react";
import Link from "next/link";
import { getTprDepartmentName } from "@/configuration/groups/tpr/athena";
import { resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import { TprManualStepContinueButton } from "@/configuration/groups/tpr/components/manual-step-continue-button";
import { TprNetworkStatus } from "@/configuration/groups/tpr/components/network-status";
import { TprOpsContinueButton } from "@/configuration/groups/tpr/components/ops-continue-button";
import { TprReceivingAssignmentContinueButton } from "@/configuration/groups/tpr/components/receiving-assignment-continue-button";
import { TprReceivingAssignmentEditor } from "@/configuration/groups/tpr/components/receiving-assignment-editor";
import { TprSyncAuthButtons } from "@/configuration/groups/tpr/components/sync-auth-buttons";
import { refreshTprWorkflowProgress, useTprLiveWorkflowRefresh } from "@/configuration/groups/tpr/components/workflow-progress";
import { isTprSpecialCaseInsurance } from "@/configuration/groups/tpr/insurance-special-cases";
import { TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION } from "@/configuration/groups/tpr/referral-auth-instructions";
import { getTprManualBypassStep, isTprContinuedFailure, isTprEligibilityTimeoutReview, isTprStoppedOnStep } from "@/configuration/groups/tpr/workflows/manual-step-continuation";
import { describeTprSpecialtyCategories, matchTprDiagnosisSpecialties } from "@/configuration/groups/tpr/workflows/physician-roster";
import { tprReferralAuthNeedsPendingUpload, tprReferralAuthNeedsReview } from "@/configuration/groups/tpr/workflows/referral-auth-status";
import type { StediEligibilityDisplayLabel, TprSyncChecklistTraceEntry, TprSyncChecklistUrgency } from "@/configuration/groups/tpr/workflows/sync-checklist";
import { RelationshipToSubscriber } from "@/packages/shared/src/models/coverage";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { useInvalidateServerComponent } from "@/src/components/server/client-provider";
import { Badge } from "@/src/components/shadcn/ui/badge";
import { AthenaCoverageEvidence } from "@/src/components/tasks/approval-types/athena-coverage-evidence";
import { AthenaCoverageResolutionDialog } from "@/src/components/tasks/athena-coverage-resolution-dialog";
import { AthenaReferralCards, AthenaReferralCardsData } from "@/src/components/tasks/athena-referral-cards";
import { ReferralEligibilityCard } from "@/src/components/tasks/referral-eligibility-card";
import { StediEligibilitySummary } from "@/src/components/tasks/stedi-eligibility-summary";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { TaskSchedulingRecommendation } from "@/src/components/tasks/task-scheduling-recommendation";
import { TaskStepCard } from "@/src/components/tasks/task-step-card";
import { useStediEligibilityCheck } from "@/src/lib/data-fetchers/stedi-eligibility-check-fetchers";
import { useTaskStepVerifications, useApproveTaskMutation, useLocalTaskUpdateMutation } from "@/src/lib/data-fetchers/tasks-fetchers";
import { findPatientContactSyncGaps, isCompleteAddress, PatientContactSyncGap } from "@/src/lib/ehr/patient-contact-sync";
import { formatFromUnderscoreToHumanReadable } from "@/src/lib/string";
import { formatDateMmDdYyyy, formatHumanReadableDate } from "@/src/lib/utils/date-and-phone-utils";
import { errorFromStepOutput } from "@/src/lib/workflows/step-failure-output";
import type { ReceivingAssignmentOutput } from "@/src/lib/workflows/steps/referral/resolve-receiving-assignment";
import type { AthenaCoverageReadbackOutput, TprInsuranceRequirementsOutput, TprReferralAuthorizationOutput } from "@/src/lib/workflows/steps/step-outputs";
import { StepKey } from "@/src/lib/workflows/steps/types";
import { getFormattedAddress } from "@/src/models/address";
import type { Appointment } from "@/src/models/appointment";
import { Coverage, CoverageType, getCoverageDisplayName, getSubscriberName } from "@/src/models/coverage";
import { type DiagnosisItem, Referral } from "@/src/models/documents/referral";
import { FileType } from "@/src/models/file";
import { getPatientDisplayName, getPatientFaxNumber, getPatientName, getPatientPhoneNumber, type Patient } from "@/src/models/patient";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import type { ProviderInsuranceRequirements } from "@/src/models/provider-insurance-requirements";
import { Task, TaskStatus, TaskType } from "@/src/models/task";
import { ApprovalType, type TaskApproval } from "@/src/models/task-approval";
import { type TaskStep, TaskStepStatus } from "@/src/models/task-step";
import { resolveTaskStepPhase, TaskStepPhase, type PinnableTaskStepPhase, type TaskStepCardState } from "@/src/models/task-step-card";
import { VerificationResult } from "@/src/models/task-verification";
const NOT_AVAILABLE = "N/A";
type TprCoverageSlot = {
  label: string;
  title: string;
};
function isTprCasePolicyCoverage(coverage: Coverage | null | undefined): boolean {
  return coverage?.coverageType === CoverageType.INSURANCE && resolveTprCasePolicyPackageId(coverage) !== null;
}
function getTprCoverageSlots(primary: Coverage | null | undefined, secondary: Coverage | null | undefined): {
  primary: TprCoverageSlot;
  secondary: TprCoverageSlot;
} {
  const primaryCasePolicy = isTprCasePolicyCoverage(primary);
  const secondaryCasePolicy = isTprCasePolicyCoverage(secondary);
  const casePolicySlot = (position: number): TprCoverageSlot => {
    const label = primaryCasePolicy && secondaryCasePolicy ? `Case Policy ${position}` : "Case Policy";
    return {
      label,
      title: label
    };
  };
  return {
    primary: primaryCasePolicy ? casePolicySlot(1) : {
      label: "Primary",
      title: "Primary Insurance"
    },
    secondary: secondaryCasePolicy ? casePolicySlot(2) : {
      label: "Secondary",
      title: "Secondary Insurance"
    }
  };
}

/**
 * Lowercased opening segment every TPR disposition note carries — see `getUnifiedReferralInternalNote`
 * in configuration/groups/tpr/workflows/referral-notes.ts. Matched anywhere in the note (not just at
 * the start) because Athena appends internal notes server-side, so ours can end up after an earlier
 * one. Used to tell "Athena already has our note" apart from "Athena has some other note" — e.g.
 * `careTeamStep`'s referring-provider address-mismatch note, which is written earlier and carries no
 * such segment.
 */
const REVIEWED_BY_VALERIE_HEALTH_MARKER = "reviewed by valerie health";

// Plain string literals rather than importing the `TprSyncBucket` enum's runtime values: this is a
// "use client" component, and `sync-checklist.ts` transitively pulls in server-only Athena/GCP client
// code, so any real (non-type-only) import from it breaks the browser bundle. Values must match
// `TprSyncBucket` in configuration/groups/tpr/workflows/sync-checklist.ts exactly.
const TPR_MANUAL_REVIEW_BUCKET = "biltmorecardreferral";

/**
 * Plain-English labels for the 3 real destinations ops actually routes to. `TPR_MANUAL_REVIEW_BUCKET`
 * is deliberately excluded — it's the decision tree's "unable to auto-determine" catch-all, not a 4th
 * destination, so it gets its own "no confident suggestion" message instead of a label here.
 */
const SUGGESTED_BUCKET_LABELS: Record<string, string> = {
  closed: "Closed — upcoming appointment already scheduled",
  sscardio: "SS Cardio — stays with practice",
  biltmorecardiosched: "Biltmore — ready to schedule"
};
const NO_CONFIDENT_BUCKET_SUGGESTION = "No confident suggestion — ops must choose SS Cardio / Biltmore (ready to schedule) / Closed based on the details above.";

/** Renders provider name / NPI as independently copyable fields (no address — that's its own line now). */
function ProviderCopyFields(t0) {
  const $ = _c(9);
  const {
    provider
  } = t0;
  let t1;
  if ($[0] !== provider) {
    t1 = provider && getPractitionerNameWithCredentials(provider) || NOT_AVAILABLE;
    $[0] = provider;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const name = t1;
  const npi = provider?.npiNumber || NOT_AVAILABLE;
  let t2;
  if ($[2] !== name) {
    t2 = <CopyableContent text={name} />;
    $[2] = name;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== npi) {
    t3 = <CopyableContent text={npi} />;
    $[4] = npi;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>Name: {t2}, NPI: {t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function ResolvedProviderCopyFields(t0) {
  const $ = _c(8);
  const {
    providerLabel,
    providerNpi,
    writeSafe
  } = t0;
  const t1 = providerLabel || NOT_AVAILABLE;
  let t2;
  if ($[0] !== t1) {
    t2 = <CopyableContent text={t1} />;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = providerNpi || NOT_AVAILABLE;
  let t4;
  if ($[2] !== t3) {
    t4 = <CopyableContent text={t3} />;
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = !writeSafe && " \u2014 verify manually before entry";
  let t6;
  if ($[4] !== t2 || $[5] !== t4 || $[6] !== t5) {
    t6 = <>Name: {t2}, NPI:{" "}{t4}{t5}</>;
    $[4] = t2;
    $[5] = t4;
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}
function getCoverageSummary(coverage: Coverage | null | undefined): string | null {
  if (!coverage) return null;
  const display = getCoverageDisplayName({
    coverage,
    includesPlan: true,
    includeSubscriberName: true
  });
  return coverage.subscriberId ? `${display} (Member ID: ${coverage.subscriberId})` : display;
}

/**
 * Who the plan is actually held by. Ops keys the subscriber into Athena separately from the
 * patient, so a plan held by a spouse or parent has to be visible on the card rather than buried
 * in "More insurance detail".
 *
 * `relationshipToSubscriber` is the *patient's* relationship to the insured, so it is phrased as
 * "patient is Child" — "John Smith (Child)" reads as though John is the child. Self is never
 * shown: `coverageSchema` fills an uncaptured relationship in as Self, so it can't be told apart
 * from a real one, and a bare "Self" would tell ops the patient holds a plan nobody captured.
 */
function getPolicyHolder(coverage: Coverage | null | undefined): string | null {
  if (!coverage || coverage.coverageType === CoverageType.SELF_PAY) return null;
  const name = getSubscriberName(coverage);
  const relationship = coverage.relationshipToSubscriber && coverage.relationshipToSubscriber !== RelationshipToSubscriber.SELF ? `patient is ${coverage.relationshipToSubscriber}` : null;
  if (!name) return relationship ? `Name not captured (${relationship})` : null;
  return relationship ? `${name} (${relationship})` : name;
}

/** One line per past appointment for the "Patient Type" sanity-check list, most-recent-first. */
function formatAppointmentHistoryEntry(appointment: Appointment): string {
  const date = appointment.appointmentDatetime ? formatHumanReadableDate(appointment.appointmentDatetime) : "TBD";
  const type = formatFromUnderscoreToHumanReadable(appointment.type) || NOT_AVAILABLE;
  const status = formatFromUnderscoreToHumanReadable(appointment.status || "") || NOT_AVAILABLE;
  return `${date} - ${type} (${status})`;
}
function sortAppointmentsMostRecentFirst(appointments: Appointment[]): Appointment[] {
  return [...appointments].sort((a, b) => {
    const aTime = a.appointmentDatetime ? new Date(a.appointmentDatetime).getTime() : 0;
    const bTime = b.appointmentDatetime ? new Date(b.appointmentDatetime).getTime() : 0;
    return bTime - aTime;
  });
}
type DataSourceEntry = {
  label: string;
  value: string;
  source: "Referral" | "Patient Chart" | "N/A";
  missing: boolean;
};

/**
 * A field only counts as truly missing if it's absent from BOTH the referral's extracted data AND
 * the patient's existing Athena chart — an extraction miss isn't "missing" if the chart already has
 * it. Prefers the referral's value when both are present, since that's what's freshest off the fax.
 */
function resolveDataSourceEntry(label: string, referralValue: string | null, chartValue: string | null): DataSourceEntry {
  if (referralValue) return {
    label,
    value: referralValue,
    source: "Referral",
    missing: false
  };
  if (chartValue) return {
    label,
    value: chartValue,
    source: "Patient Chart",
    missing: false
  };
  return {
    label,
    value: NOT_AVAILABLE,
    source: "N/A",
    missing: true
  };
}
type TimelineStatus = TaskStepCardState;

/**
 * Stable identity for each TPR checklist step, used as `task_step_verification.step_key`
 * so recorded accuracy stays comparable when steps are renumbered, retitled, or reordered. Never
 * derive this from `number` or `title` — both change; these slugs must not.
 */
export const TPR_TASK_STEP_KEY = {
  LOCATE_DOCUMENT: "locate_document",
  PROVIDER_LOCATION: "provider_location",
  DEMOGRAPHICS_CARE_TEAM: "demographics_care_team",
  INSURANCE: "insurance",
  REFERRAL_AUTHORIZATION: "referral_authorization",
  OUTBOUND_FAXES: "outbound_faxes",
  FULL_REGISTRATION: "full_registration",
  PROCESS_REFERRAL_DOCUMENT: "process_referral_document",
  DOCUMENT_UPLOAD: "document_upload",
  QUICKVIEW: "quickview",
  BUCKET_ASSIGNMENT: "bucket_assignment"
} as const;
export type TprTaskStepKey = (typeof TPR_TASK_STEP_KEY)[keyof typeof TPR_TASK_STEP_KEY];
type TimelineStep = {
  number: number;
  title: string;
  stepKey: TprTaskStepKey;
  status: TimelineStatus;
  /** See `TPR_PINNED_STEP_PHASE`. Omitted means the phase follows the status. */
  phase?: PinnableTaskStepPhase;
  /** Why this step needs a human, in place of the card's generic instruction. */
  detail?: string;
  summary: string;
  workflowStepKeys: StepKey[];
  /**
   * The few fields worth seeing on a card that is still waiting its turn — what automation
   * decided, without the evidence behind it. Items with no value are dropped, so a step the
   * workflow hasn't reached shows nothing rather than a row of N/A.
   */
  preview?: DecisionMetadataItem[];
  content?: React.ReactNode;
  continuation?: {
    instruction: string;
    control?: React.ReactNode;
  };
};

/**
 * Steps whose phase is a property of the step itself, not of how cleanly automation happened to
 * resolve it on this referral.
 *
 * These are the TPR steps automation was never built to finish: outbound faxes are still
 * partially manual, the referral document needs a human to key parts of it into Athena, and the
 * bucket is ops' judgement call. They stay `ACTION_NEEDED` even when every workflow step under
 * them completed, because "completed" there only means the automated part is done — the human
 * part still isn't.
 *
 * Referral Authorization is pinned per-referral rather than here: it's `ACTION_NEEDED` only when
 * it needs an outbound fax, and plain `QA` when adding the authorization object is the whole job.
 *
 * Every other step is automated end to end, so it falls through to its status: `QA` when it
 * worked, `FAILED` when it didn't. A failed sync outranks a pin — see `resolveTaskStepPhase`.
 */
const TPR_PINNED_STEP_PHASE: Partial<Record<TprTaskStepKey, PinnableTaskStepPhase>> = {
  [TPR_TASK_STEP_KEY.OUTBOUND_FAXES]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT]: TaskStepPhase.ACTION_NEEDED,
  [TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT]: TaskStepPhase.ACTION_NEEDED
};
export type DecisionMetadataItem = {
  label: string;
  value: React.ReactNode;
};
export type TprChartReferralAuth = {
  referralAuthId: string;
  number: string | null;
  startDate: string | null;
  endDate: string | null;
  expired: boolean | null;
};
export type TprChartRequestLetter = {
  documentEhrId: string;
  documentDate: string | null;
  description: string | null;
};
type TprOpsCrossCheck = {
  patientName: string;
  patientDateOfBirth: string;
  patientHomePhoneNumber: string;
  patientMobilePhoneNumber: string;
  patientWorkPhoneNumber: string;
  patientEmail: string;
  patientFaxNumber: string;
  patientEthnicity: string;
  patientRace: string;
  patientLanguage: string;
  patientHomeAddress: string;
  receivingProviderName: string;
  receivingProviderNpi: string;
  referringProviderName: string;
  referringProviderNpi: string;
  referringProviderSpecialty: string;
  referringLocationName: string;
  referringLocationAddress: string;
  emergencyContactName: string;
  emergencyContactPhoneNumber: string;
  emergencyContactRelationship: string;
  dateReceived: string;
  primaryPlanLabel: string;
  primarySubscriberId: string;
  primaryPolicyHolder: string;
  /** Null when the patient genuinely has no secondary plan, as opposed to an uncaptured one. */
  secondaryPlanLabel: string | null;
  secondarySubscriberId: string | null;
  secondaryPolicyHolder: string | null;
  isSecondarySpecialCaseInsurance: boolean;
  /**
   * The authorization terms as extracted from the referral. Ops needs the whole set — a number with
   * no validity window or visit count can't be checked against what Athena holds.
   */
  authorizationNumber: string;
  authorizationStatus: string;
  authorizationStartDate: string;
  authorizationEndDate: string;
  authorizationVisits: string;
  /** Separate from `authorizationVisits`: "services" and "visits" are distinct extracted fields. */
  authorizationServices: string | null;
  /** PIMC / Triwest / MercyCare — special-case payers ops wants visually flagged on Insurance. */
  isSpecialCaseInsurance: boolean;
  primaryCoverageSlot: TprCoverageSlot;
  secondaryCoverageSlot: TprCoverageSlot;
  diagnosis: string;
  /** Which of TPR's four sub-specialty categories the diagnosis text maps to (General /
   * Interventional / Structural / EP) — the bucket the provider resolver matched providers against. */
  diagnosisCategories: string;
};

/**
 * The condensed body of a card that hasn't come up yet.
 *
 * Drops items with no value, so a step the workflow genuinely hasn't reached renders nothing
 * instead of a row of N/A — the point is to show what was decided, and nothing has been yet.
 */
function TimelineStepPreview(t0) {
  const $ = _c(6);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items ?? [];
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.filter(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const resolved = t2;
  let t3;
  if ($[4] !== resolved) {
    t3 = resolved.length > 0 ? <DecisionMetadata items={resolved} /> : null;
    $[4] = resolved;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  return t3;
}
function _temp(item) {
  return item.value && item.value !== NOT_AVAILABLE;
}
export function DecisionMetadata(t0) {
  const $ = _c(4);
  const {
    items
  } = t0;
  let t1;
  if ($[0] !== items) {
    t1 = items.map(_temp2);
    $[0] = items;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <dl className="flex flex-wrap gap-2">{t1}</dl>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
}
function _temp2(item) {
  return <div key={item.label} className="inline-flex items-center gap-1.5 rounded-md border bg-muted/30 px-2 py-1 text-xs"><dt className="text-muted-foreground">{item.label}</dt><dd className="font-medium text-foreground">{item.value}</dd></div>;
}
type ProviderSelectionEvidence = NonNullable<ReceivingAssignmentOutput["providerSelectionEvidence"]>;
type ProviderSelectionStep = NonNullable<ProviderSelectionEvidence["selectedStep"]>;
function providerSelectionReason(selectedStep: ProviderSelectionEvidence["selectedStep"], internalReferral = false): string {
  switch (selectedStep) {
    case "upcoming_appointment":
      return "Selected from the earliest qualifying Biltmore appointment within 60 days.";
    case "past_appointment":
      return "Selected from the most recent qualifying completed Biltmore appointment in the past three years.";
    case "usual_provider":
      return "Legacy selection used the Athena usual provider; verify this assignment against appointment history.";
    case "referral_provider":
      return internalReferral ? "Selected because this is an internal Biltmore-to-Biltmore referral, so the provider named on the referral takes precedence over appointment history." : "Selected because this provider is named on the referral.";
    case "diagnosis_match":
      return "Selected because this provider treats the referral diagnosis at the chosen location.";
    default:
      return "No provider was selected automatically.";
  }
}
function ProviderSelectionChecklist(t0) {
  const $ = _c(37);
  const {
    evidence,
    upcomingAppointmentProviderSummary,
    referralProviderName,
    referralProviderNpi,
    diagnosis,
    diagnosisCategories,
    selectedProviderName,
    selectedLocation
  } = t0;
  let t1;
  if ($[0] !== evidence.appointmentProviders) {
    t1 = evidence.appointmentProviders.some(_temp3);
    $[0] = evidence.appointmentProviders;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const hasQualificationEvidence = t1;
  let t2;
  if ($[2] !== evidence.appointmentProviders || $[3] !== evidence.selectedStep || $[4] !== hasQualificationEvidence || $[5] !== upcomingAppointmentProviderSummary) {
    t2 = period => {
      const providers = evidence.appointmentProviders.flatMap(provider_0 => {
        const count = period === "past" ? provider_0.qualifyingPastAppointmentCount ?? 0 : provider_0.qualifyingUpcomingAppointmentCount ?? 0;
        const locationEhrId = period === "past" ? provider_0.mostRecentPastLocationEhrId : provider_0.earliestUpcomingLocationEhrId;
        const location = locationEhrId ? getTprDepartmentName(locationEhrId) ?? `Athena department ${locationEhrId}` : null;
        return count > 0 ? [`${provider_0.providerName}${provider_0.providerNpi ? ` · ${provider_0.providerNpi}` : ""}${location ? ` · ${location}` : ""} (${count})`] : [];
      });
      if (providers.length > 0) {
        return providers.join(", ");
      }
      if (period === "upcoming" && !hasQualificationEvidence && upcomingAppointmentProviderSummary) {
        return upcomingAppointmentProviderSummary;
      }
      if (!hasQualificationEvidence && (evidence.selectedStep === "usual_provider" || evidence.appointmentProviders.length > 0)) {
        return "Not captured for this legacy task";
      }
      return "None";
    };
    $[2] = evidence.appointmentProviders;
    $[3] = evidence.selectedStep;
    $[4] = hasQualificationEvidence;
    $[5] = upcomingAppointmentProviderSummary;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const providerCountSummary = t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[7] !== diagnosis || $[8] !== diagnosisCategories || $[9] !== evidence.internalReferral || $[10] !== evidence.pastAppointmentCount || $[11] !== evidence.selectedStep || $[12] !== evidence.upcomingAppointmentCount || $[13] !== providerCountSummary || $[14] !== referralProviderName || $[15] !== referralProviderNpi || $[16] !== selectedLocation || $[17] !== selectedProviderName) {
    const upcomingAppointmentStep = {
      key: "upcoming_appointment" as const,
      title: "Check for an upcoming appointment",
      items: [{
        label: "Upcoming within 60 days",
        value: evidence.upcomingAppointmentCount
      }, {
        label: "Qualifying appointments at Biltmore locations",
        value: providerCountSummary("upcoming")
      }]
    };
    const pastAppointmentStep = {
      key: "past_appointment" as const,
      title: "Check past appointments at Biltmore locations",
      items: [{
        label: "Completed in past 3 years",
        value: evidence.pastAppointmentCount
      }, {
        label: "Qualifying appointments at Biltmore locations",
        value: providerCountSummary("past")
      }]
    };
    const referralProviderStep = {
      key: "referral_provider" as const,
      title: "Use the provider named on the referral",
      items: [{
        label: "Provider",
        value: referralProviderName
      }, {
        label: "NPI",
        value: referralProviderNpi
      }]
    };
    const diagnosisMatchStep = {
      key: "diagnosis_match" as const,
      title: "Sanity-check the pick or find a replacement",
      items: [{
        label: "Diagnosis",
        value: diagnosis
      }, {
        label: "Diagnosis category",
        value: diagnosisCategories
      }, {
        label: "Location",
        value: selectedLocation
      }, {
        label: "Final provider",
        value: selectedProviderName
      }]
    };
    const steps = evidence.internalReferral ? [referralProviderStep, upcomingAppointmentStep, pastAppointmentStep, diagnosisMatchStep] : [upcomingAppointmentStep, pastAppointmentStep, referralProviderStep, diagnosisMatchStep];
    t6 = "space-y-2 border-t pt-3";
    if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <summary className="cursor-pointer text-sm font-medium">Find the Provider and Location</summary>;
      $[23] = t7;
    } else {
      t7 = $[23];
    }
    t5 = "mt-2 space-y-2";
    t3 = "space-y-2";
    t4 = steps.map((step, index) => {
      const selected = evidence.selectedStep === step.key;
      return <li key={step.key} className={selected ? "space-y-2 rounded-md border border-foreground/40 bg-muted p-3" : "space-y-2 rounded-md border bg-muted/10 p-3"}><div className="flex items-center justify-between gap-3"><span className="text-sm font-medium">Step {index + 1} — {step.title}</span>{selected ? <Badge variant="outline" className="border-foreground/40 bg-muted font-medium text-foreground">Selected</Badge> : null}</div><DecisionMetadata items={selected ? [...step.items, {
          label: "Picked provider",
          value: selectedProviderName
        }, {
          label: "Reason",
          value: providerSelectionReason(evidence.selectedStep, evidence.internalReferral)
        }] : step.items} /></li>;
    });
    $[7] = diagnosis;
    $[8] = diagnosisCategories;
    $[9] = evidence.internalReferral;
    $[10] = evidence.pastAppointmentCount;
    $[11] = evidence.selectedStep;
    $[12] = evidence.upcomingAppointmentCount;
    $[13] = providerCountSummary;
    $[14] = referralProviderName;
    $[15] = referralProviderNpi;
    $[16] = selectedLocation;
    $[17] = selectedProviderName;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
  } else {
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
  }
  let t8;
  if ($[24] !== t3 || $[25] !== t4) {
    t8 = <ol className={t3}>{t4}</ol>;
    $[24] = t3;
    $[25] = t4;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== evidence.selectedStep) {
    t9 = evidence.selectedStep === "usual_provider" ? <div className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-warn-foreground">This assignment was created with the retired Athena usual-provider rule. Verify the provider and location against qualifying appointment history before following it.</div> : null;
    $[27] = evidence.selectedStep;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t5 || $[30] !== t8 || $[31] !== t9) {
    t10 = <div className={t5}>{t8}{t9}</div>;
    $[29] = t5;
    $[30] = t8;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t6 || $[35] !== t7) {
    t11 = <details className={t6}>{t7}{t10}</details>;
    $[33] = t10;
    $[34] = t6;
    $[35] = t7;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  return t11;
}
function _temp3(provider) {
  return provider.qualifyingUpcomingAppointmentCount !== undefined || provider.qualifyingPastAppointmentCount !== undefined;
}
function taskStepStatus(taskSteps: TaskStep[], keys: StepKey[]): TimelineStatus {
  const rows = keys.map(key => taskSteps.find(step => step.stepKey === key)).filter(Boolean);
  if (rows.some(row => row?.status === TaskStepStatus.FAILED)) return "failed";
  if (rows.some(row => row?.status === TaskStepStatus.ASYNC || row?.status === TaskStepStatus.PAUSED)) {
    return "waiting";
  }
  if (keys.every(key => taskSteps.some(step => step.stepKey === key && step.status === TaskStepStatus.COMPLETED))) {
    return "completed";
  }
  return "waiting";
}
function TimelineStepCard(t0) {
  const $ = _c(32);
  const {
    task,
    step,
    failedStepKey,
    bypassStepKey,
    failureReason,
    continuedPastFailure,
    signOffOnContinue,
    blockedBy,
    awaitingStep
  } = t0;
  const effectiveStatus = failedStepKey ? "failed" : awaitingStep ? "not_run" : step.status;
  const approvalMutation = useApproveTaskMutation();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      invalidateOnSettled: false
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const localTaskUpdateMutation = useLocalTaskUpdateMutation(t1);
  const invalidateInstructions = useInvalidateServerComponent("getReferralInstructions");
  let t2;
  if ($[1] !== continuedPastFailure || $[2] !== failedStepKey || $[3] !== failureReason || $[4] !== signOffOnContinue || $[5] !== step || $[6] !== task) {
    t2 = failedStepKey ? {
      instruction: failedStepKey === StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY ? "The eligibility check failed. Verify or correct the coverage in Athena, then recheck eligibility to save the result before continuing." : continuedPastFailure ? `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. The rest of the workflow ran anyway. Complete this step in Athena, then continue.` : `The ${formatFromUnderscoreToHumanReadable(failedStepKey).toLowerCase()} step failed${failureReason ? ` (${failureReason})` : ""}. Complete it manually in Athena, then continue with the remaining steps.`,
      control: <TprManualStepContinueButton task={task} stepKey={failedStepKey} signOff={signOffOnContinue ? step : undefined} />
    } : step.continuation;
    $[1] = continuedPastFailure;
    $[2] = failedStepKey;
    $[3] = failureReason;
    $[4] = signOffOnContinue;
    $[5] = step;
    $[6] = task;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const continuation = t2;
  let t3;
  if ($[8] !== approvalMutation || $[9] !== invalidateInstructions || $[10] !== localTaskUpdateMutation || $[11] !== task) {
    t3 = async () => {
      await approvalMutation.mutateAsync({
        taskId: task.id,
        approved: true
      });
      localTaskUpdateMutation.mutate({
        ...task,
        status: TaskStatus.PROCESSING
      });
      refreshTprWorkflowProgress(invalidateInstructions);
    };
    $[8] = approvalMutation;
    $[9] = invalidateInstructions;
    $[10] = localTaskUpdateMutation;
    $[11] = task;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleVerificationComplete = t3;
  const t4 = `${step.number}. ${step.title}`;
  const t5 = blockedBy ? `Waiting on step ${blockedBy.number} (${blockedBy.title}), which failed.` : !continuation?.control && continuation?.instruction || step.detail;
  const t6 = step.continuation ? handleVerificationComplete : undefined;
  let t7;
  if ($[13] !== bypassStepKey || $[14] !== continuation || $[15] !== task) {
    t7 = continuation?.control || bypassStepKey ? <div className="space-y-3">{continuation?.instruction ? <p className="text-sm text-muted-foreground">{continuation.instruction}</p> : null}{continuation?.control ? <div className="flex justify-center">{continuation.control}</div> : null}{bypassStepKey ? <div className="space-y-2"><p className="text-sm text-muted-foreground">Continue past this check without marking it verified. Your override will be recorded.</p><TprManualStepContinueButton task={task} stepKey={bypassStepKey} bypass={true} /></div> : null}</div> : undefined;
    $[13] = bypassStepKey;
    $[14] = continuation;
    $[15] = task;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== awaitingStep || $[18] !== step.content || $[19] !== step.preview) {
    t8 = awaitingStep ? <TimelineStepPreview items={step.preview} /> : step.content;
    $[17] = awaitingStep;
    $[18] = step.content;
    $[19] = step.preview;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== effectiveStatus || $[22] !== step.phase || $[23] !== step.stepKey || $[24] !== step.workflowStepKeys || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7 || $[29] !== t8 || $[30] !== task.id) {
    t9 = <TaskStepCard header={t4} status={effectiveStatus} phase={step.phase} detail={t5} taskId={task.id} stepKey={step.stepKey} workflowStepKeys={step.workflowStepKeys} onVerificationComplete={t6} action={t7}>{t8}</TaskStepCard>;
    $[21] = effectiveStatus;
    $[22] = step.phase;
    $[23] = step.stepKey;
    $[24] = step.workflowStepKeys;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
    $[30] = task.id;
    $[31] = t9;
  } else {
    t9 = $[31];
  }
  return t9;
}
function TprStediEligibilityValues(t0) {
  const $ = _c(5);
  const {
    referralId
  } = t0;
  const {
    eligibilityCheck
  } = useStediEligibilityCheck(referralId);
  let t1;
  if ($[0] !== eligibilityCheck) {
    t1 = <StediEligibilitySummary check={eligibilityCheck} showHeader={false} />;
    $[0] = eligibilityCheck;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <p className="text-sm text-muted-foreground">Coverage rechecks and edits are available on the Information tab.</p>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t1) {
    t3 = <div className="space-y-3">{t1}{t2}</div>;
    $[3] = t1;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function TprSyncTimeline(t0) {
  const $ = _c(99);
  const {
    task,
    referralId,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    athenaCoverageLive,
    insuranceRequirements,
    referralAuthorization,
    resolvedReceiving,
    patientLabel,
    patientEhrId,
    documentId,
    bucketLabel,
    internalNoteToEnter,
    urgency,
    actionNote,
    decisionTrace,
    insuranceStatusDisplay,
    hasMissingInformation,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    upcomingAppointments,
    pastAppointments,
    documentUploadLabels,
    privacyNoticeOnFile,
    referralSourceOnFile,
    isPrimaryCareProvider,
    referringProviderMarkedPcp,
    primaryCareProviderAlreadyPresent,
    patientDisplayId,
    opsCrossCheck,
    patientContactSyncGaps,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  useTprLiveWorkflowRefresh(useInvalidateServerComponent("getReferralInstructions"));
  const {
    verdictByStepKey
  } = useTaskStepVerifications(task.id);
  let t1;
  if ($[0] !== verdictByStepKey) {
    t1 = verdictByStepKey.get(TPR_TASK_STEP_KEY.PROVIDER_LOCATION)?.verdict;
    $[0] = verdictByStepKey;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const providerAssignmentMarkedIncorrect = t1 === VerificationResult.INCORRECT;
  let t2;
  if ($[2] !== taskSteps) {
    t2 = taskSteps.some(_temp4);
    $[2] = taskSteps;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const isProviderFirst = t2;
  let t3;
  if ($[4] !== pastAppointments || $[5] !== resolvedReceiving.providerSelectionEvidence || $[6] !== upcomingAppointments) {
    t3 = resolvedReceiving.providerSelectionEvidence ?? {
      selectedStep: null,
      upcomingAppointmentCount: upcomingAppointments.length,
      pastAppointmentCount: pastAppointments.length,
      internalReferral: false,
      appointmentProviders: []
    };
    $[4] = pastAppointments;
    $[5] = resolvedReceiving.providerSelectionEvidence;
    $[6] = upcomingAppointments;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const providerSelectionEvidence = t3;
  const workflowStillProcessing = task.status === TaskStatus.PROCESSING;
  let t4;
  if ($[8] !== actionNote || $[9] !== athenaCoverageLive.primary || $[10] !== athenaCoverageLive.secondary || $[11] !== bucketLabel || $[12] !== chartReferralAuths || $[13] !== chartRequestLetters || $[14] !== coverageReadback || $[15] !== decisionTrace || $[16] !== documentId || $[17] !== documentUploadLabels || $[18] !== followThroughEligible || $[19] !== hasMissingInformation || $[20] !== insuranceRequirements || $[21] !== insuranceStatusDisplay || $[22] !== internalNoteToEnter || $[23] !== isPrimaryCareProvider || $[24] !== isProviderFirst || $[25] !== opsCrossCheck.authorizationEndDate || $[26] !== opsCrossCheck.authorizationNumber || $[27] !== opsCrossCheck.authorizationServices || $[28] !== opsCrossCheck.authorizationStartDate || $[29] !== opsCrossCheck.authorizationStatus || $[30] !== opsCrossCheck.authorizationVisits || $[31] !== opsCrossCheck.dateReceived || $[32] !== opsCrossCheck.diagnosis || $[33] !== opsCrossCheck.diagnosisCategories || $[34] !== opsCrossCheck.emergencyContactName || $[35] !== opsCrossCheck.emergencyContactPhoneNumber || $[36] !== opsCrossCheck.emergencyContactRelationship || $[37] !== opsCrossCheck.isSecondarySpecialCaseInsurance || $[38] !== opsCrossCheck.isSpecialCaseInsurance || $[39] !== opsCrossCheck.patientDateOfBirth || $[40] !== opsCrossCheck.patientEmail || $[41] !== opsCrossCheck.patientEthnicity || $[42] !== opsCrossCheck.patientFaxNumber || $[43] !== opsCrossCheck.patientHomeAddress || $[44] !== opsCrossCheck.patientHomePhoneNumber || $[45] !== opsCrossCheck.patientLanguage || $[46] !== opsCrossCheck.patientMobilePhoneNumber || $[47] !== opsCrossCheck.patientName || $[48] !== opsCrossCheck.patientRace || $[49] !== opsCrossCheck.patientWorkPhoneNumber || $[50] !== opsCrossCheck.primaryCoverageSlot.title || $[51] !== opsCrossCheck.primaryPlanLabel || $[52] !== opsCrossCheck.primaryPolicyHolder || $[53] !== opsCrossCheck.primarySubscriberId || $[54] !== opsCrossCheck.receivingProviderName || $[55] !== opsCrossCheck.receivingProviderNpi || $[56] !== opsCrossCheck.referringLocationAddress || $[57] !== opsCrossCheck.referringProviderName || $[58] !== opsCrossCheck.referringProviderNpi || $[59] !== opsCrossCheck.referringProviderSpecialty || $[60] !== opsCrossCheck.secondaryCoverageSlot.title || $[61] !== opsCrossCheck.secondaryPlanLabel || $[62] !== opsCrossCheck.secondaryPolicyHolder || $[63] !== opsCrossCheck.secondarySubscriberId || $[64] !== patientContactSyncGaps || $[65] !== patientDisplayId || $[66] !== patientEhrId || $[67] !== patientLabel || $[68] !== pendingApproval || $[69] !== primaryCareProviderAlreadyPresent || $[70] !== privacyNoticeOnFile || $[71] !== providerAssignmentMarkedIncorrect || $[72] !== providerSelectionEvidence || $[73] !== receivingAssignmentConfirmed || $[74] !== referralAuthorization || $[75] !== referralId || $[76] !== referralSourceOnFile || $[77] !== referringProviderMarkedPcp || $[78] !== resolvedReceiving.advisories || $[79] !== resolvedReceiving.flags || $[80] !== resolvedReceiving.locationEhrId || $[81] !== resolvedReceiving.locationLabel || $[82] !== resolvedReceiving.providerLabel || $[83] !== resolvedReceiving.providerNpi || $[84] !== resolvedReceiving.providerWriteSafe || $[85] !== resolvedReceiving.routedToSscardio || $[86] !== resolvedReceiving.usualProviderWriteSafe || $[87] !== task || $[88] !== taskSteps || $[89] !== unresolvedUpcomingProviders || $[90] !== upcomingAppointmentProviderSummary || $[91] !== urgency.isUrgent || $[92] !== urgency.reason || $[93] !== urgency.schedulingPriority || $[94] !== verdictByStepKey || $[95] !== workflowStillProcessing) {
    const assignmentStatus = isProviderFirst ? taskStepStatus(taskSteps, [StepKey.RESOLVE_RECEIVING_ASSIGNMENT]) : workflowStillProcessing ? "waiting" : "not_run";
    const assignmentDependentStatus = assignmentStatus === "failed" ? "not_run" : assignmentStatus;
    const receivingAssignmentApproval = pendingApproval?.approvalType === ApprovalType.TPR_RECEIVING_ASSIGNMENT ? pendingApproval : null;
    const assignmentRequiresManualReview = assignmentStatus === "completed" && !resolvedReceiving.providerWriteSafe && receivingAssignmentApproval !== null;
    const usesRetiredUsualProviderRule = providerSelectionEvidence.selectedStep === "usual_provider";
    const assignmentNeedsManualConfirmation = !resolvedReceiving.providerWriteSafe || usesRetiredUsualProviderRule;
    const assignmentWasManuallyConfirmed = assignmentStatus === "completed" && assignmentNeedsManualConfirmation && receivingAssignmentConfirmed;
    const assignmentReviewStatus = assignmentWasManuallyConfirmed ? "completed" : assignmentRequiresManualReview || usesRetiredUsualProviderRule ? "verification_required" : assignmentDependentStatus;
    const assignmentNeedsReview = !assignmentWasManuallyConfirmed && (assignmentRequiresManualReview || usesRetiredUsualProviderRule);
    const gateStatus = taskStepStatus(taskSteps, [StepKey.TPR_CAMELBACK_FOLLOW_THROUGH]);
    const assignmentSelectionIncomplete = !resolvedReceiving.providerLabel?.trim() || !resolvedReceiving.providerNpi?.trim() || !resolvedReceiving.locationLabel?.trim();
    const assignmentResolved = assignmentStatus === "completed" || assignmentStatus === "failed";
    const assignmentConfirmationStatus = assignmentResolved && assignmentSelectionIncomplete ? "failed" : assignmentWasManuallyConfirmed ? "completed" : assignmentReviewStatus === "verification_required" ? "verification_required" : assignmentStatus === "completed" ? gateStatus : assignmentStatus;
    const providerAssignmentEditable = providerAssignmentMarkedIncorrect || assignmentConfirmationStatus === "failed";
    const confirmedNonCamelbackAssignment = assignmentStatus === "completed" && !followThroughEligible && receivingAssignmentApproval === null;
    const skippedFollowThroughStatus = confirmedNonCamelbackAssignment ? "not_needed" : "not_run";
    const skippedFollowThroughSummary = assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : confirmedNonCamelbackAssignment ? "Not needed for the resolved non-Camelback receiving assignment." : "Not run because the receiving assignment has not completed.";
    const coverageApproval = pendingApproval?.approvalType === ApprovalType.ATHENA_COVERAGE ? pendingApproval : null;
    const eligibilityTimedOut = taskSteps.some(step_0 => isTprEligibilityTimeoutReview(step_0) && isTprStoppedOnStep(task.status, taskSteps, step_0));
    const opsApproval = pendingApproval?.approvalType === ApprovalType.TPR_REFERRAL_OPS ? pendingApproval : null;
    const opsKind = opsApproval?.metadata?.kind ?? null;
    const manualBypassStepKey = getTprManualBypassStep(task.status, taskSteps, pendingApproval)?.stepKey as StepKey | undefined;
    const waitingForAssociatedReferral = taskSteps.some(_temp5);
    const coverageReadbackComplete = taskSteps.some(_temp6);
    const authRequestUploaded = taskSteps.some(_temp7);
    const notesComplete = taskSteps.some(_temp8);
    const missingInfoUploadComplete = taskSteps.some(_temp9);
    const followThroughStatus = keys => followThroughEligible ? taskStepStatus(taskSteps, keys) : skippedFollowThroughStatus;
    const resolveConsolidatedStatus = _temp10;
    const livePrimaryAthenaPlan = athenaCoverageLive.primary && getCoverageDisplayName({
      coverage: athenaCoverageLive.primary
    }) || coverageReadback?.primary.planName || NOT_AVAILABLE;
    const liveSecondaryAthenaPlan = athenaCoverageLive.secondary && getCoverageDisplayName({
      coverage: athenaCoverageLive.secondary
    }) || coverageReadback?.secondary?.planName || NOT_AVAILABLE;
    const athenaCoverageDrifted = Boolean(coverageReadback && athenaCoverageLive.primary?.ehrId && athenaCoverageLive.primary.ehrId !== coverageReadback.primary.insuranceId);
    const primaryCoverageInAthena = Boolean(athenaCoverageLive.primary?.ehrId ?? coverageReadback?.primary.insuranceId);
    const insuranceStatus = primaryCoverageInAthena ? "completed" : workflowStillProcessing ? "waiting" : "failed";
    const authorizationStatus = opsKind === "authorization_review" ? "verification_required" : opsKind === "manual_authorization" ? "review_needed" : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "review_needed" : referralAuthorization?.outcome === "not_needed" ? "not_needed" : referralAuthorization?.outcome === "needs_pending_request" ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTHORIZATION]);
    const referralAuthNeedsPendingUpload = tprReferralAuthNeedsPendingUpload(referralAuthorization);
    const referralAuthNeedsOutboundFax = Boolean(insuranceRequirements?.referralMustBeFromPcp) || referralAuthNeedsPendingUpload;
    const requirementApplies = _temp11;
    const showPcpReferralRequirement = Boolean(insuranceRequirements?.referralMustBeFromPcp);
    const showAuthorizationRequirement = requirementApplies(insuranceRequirements?.authorizationRequirement);
    const showReferralRequirement = showAuthorizationRequirement || requirementApplies(insuranceRequirements?.referralRequirement);
    const createdChartReferralAuthId = referralAuthorization?.outcome === "created" ? referralAuthorization.referralAuthId : null;
    const documentAuthorizationNumber = opsCrossCheck.authorizationNumber === NOT_AVAILABLE ? null : opsCrossCheck.authorizationNumber.toLowerCase();
    const documentChartReferralAuths = chartReferralAuths.filter(authorization => authorization.referralAuthId === createdChartReferralAuthId || documentAuthorizationNumber !== null && authorization.number?.trim().toLowerCase() === documentAuthorizationNumber);
    const authorizationDetails = <div className="space-y-3"><DecisionMetadata items={[{
        label: "Auth number",
        value: <CopyableContent text={opsCrossCheck.authorizationNumber} textClassName="text-xs" />
      }, {
        label: "Status",
        value: opsCrossCheck.authorizationStatus
      }, {
        label: "Start date",
        value: opsCrossCheck.authorizationStartDate
      }, {
        label: "End date",
        value: opsCrossCheck.authorizationEndDate
      }, {
        label: "Visits",
        value: opsCrossCheck.authorizationVisits
      }, ...(opsCrossCheck.authorizationServices ? [{
        label: "Services",
        value: opsCrossCheck.authorizationServices
      }] : []), {
        label: "Outcome",
        value: referralAuthorization ? formatFromUnderscoreToHumanReadable(referralAuthorization.outcome) : "Pending"
      }, ...(referralAuthorization?.outcome === "created" ? [{
        label: "Athena object",
        value: <CopyableContent text={referralAuthorization.referralAuthId} textClassName="text-xs" />
      }, {
        label: "Appointment",
        value: referralAuthorization.needsManualAppointmentAttach ? "Attach manually" : referralAuthorization.appointmentAttached ? "Attached" : "None in window"
      }] : [])]} />{documentChartReferralAuths.length ? <details className="text-sm"><summary className="cursor-pointer font-medium">On the Athena chart</summary><div className="mt-2 space-y-2"><ul className="list-disc space-y-1 pl-5">{documentChartReferralAuths.map(_temp12)}</ul>{chartRequestLetters.length ? <div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Request letters</p><ul className="list-disc space-y-1 pl-5">{chartRequestLetters.map(_temp13)}</ul></div> : null}</div></details> : null}<TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)} taskId={task.id} /></div>;
    const pcpFaxStatus = opsKind === "pcp_referral_fax" ? "review_needed" : insuranceRequirements && !insuranceRequirements.referralMustBeFromPcp ? "not_needed" : followThroughStatus([StepKey.TPR_PCP_REFERRAL_FAX]);
    const authRequestUploadStatus = opsKind === "auth_request_upload" ? "review_needed" : referralAuthNeedsPendingUpload ? authRequestUploaded ? "completed" : followThroughStatus([StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD]) : "not_needed";
    const missingInfoFaxStatus = !hasMissingInformation ? "not_needed" : missingInfoUploadComplete ? "review_needed" : followThroughStatus([StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX]);
    const outboundFaxStatus = resolveConsolidatedStatus([authRequestUploadStatus, missingInfoFaxStatus]);
    const closeDocumentStatus = opsKind === "close_document" ? "review_needed" : followThroughStatus([StepKey.TPR_CLOSE_DOCUMENT]);
    const workflowNote = task.files.find(_temp14)?.note;
    const ensurePatientStatus = taskStepStatus(taskSteps, [StepKey.ENSURE_PATIENT]);
    const patientChartStatus = !patientEhrId || patientEhrId === NOT_AVAILABLE ? "review_needed" : "verification_required";
    const fullRegistrationReviewReasons = [privacyNoticeOnFile === false || workflowNote?.includes("Athena privacy notice could not be set automatically") ? "Set Athena Privacy to YES manually." : null, ensurePatientStatus === "completed" && referralSourceOnFile === null ? "Set \"How did you hear about us\" to PHYSICIAN REFERRAL manually." : null].filter(_temp15);
    const demographicsReviewReasons = [workflowNote?.includes("could not be matched in Athena and was not added to the patient's care team") ? "Add the unmatched referring provider to the Athena care team manually." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Phone) ? "Enter the patient's phone number in Athena manually \u2014 it did not sync." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.Email) ? "Enter the patient's email in Athena manually \u2014 it did not sync." : null, ensurePatientStatus === "completed" && patientContactSyncGaps.includes(PatientContactSyncGap.HomeAddress) ? "Enter the patient's home address in Athena manually \u2014 it did not sync." : null].filter(_temp16);
    const pcpDisposition = referringProviderMarkedPcp ? "Yes \u2014 marked Is PCP at intake" : !isPrimaryCareProvider ? "No \u2014 not a primary care specialty" : primaryCareProviderAlreadyPresent ? "Already the patient's PCP in Athena" : "Yes \u2014 added as the patient's PCP";
    const careTeamStatus = taskStepStatus(taskSteps, [StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM]);
    const fullRegistrationStatus = ensurePatientStatus === "failed" ? "not_run" : ensurePatientStatus === "waiting" ? "waiting" : fullRegistrationReviewReasons.length > 0 ? "failed" : privacyNoticeOnFile === null ? "verification_required" : "completed";
    const hasEmergencyContactToEnter = opsCrossCheck.emergencyContactName !== NOT_AVAILABLE || opsCrossCheck.emergencyContactPhoneNumber !== NOT_AVAILABLE;
    const demographicsStatus = ensurePatientStatus === "failed" ? "not_run" : careTeamStatus !== "completed" ? careTeamStatus : demographicsReviewReasons.length > 0 ? "failed" : "completed";
    const dispositionStatus = taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION]);
    const unevaluatedInsuranceDecision = followThroughEligible ? "Pending" : "Not evaluated";
    const inNetworkDisplay = !insuranceRequirements ? unevaluatedInsuranceDecision : insuranceRequirements.clinicNetwork === "out_of_network" || insuranceRequirements.providerNetwork === "out_of_network" ? "No" : insuranceRequirements.clinicNetwork === "in_network" && insuranceRequirements.providerNetwork === "in_network" ? insuranceRequirements.networkVerification ? "Yes \u2014 confirmed by Ops" : "Yes \u2014 confirmed by insurance grid" : insuranceRequirements.clinicNetwork === "not_applicable" && insuranceRequirements.providerNetwork === "not_applicable" ? "Not applicable" : "Manual review";
    const referralRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.referralRequirement) : unevaluatedInsuranceDecision;
    const authorizationRequirementDisplay = insuranceRequirements ? formatFromUnderscoreToHumanReadable(insuranceRequirements.authorizationRequirement) : unevaluatedInsuranceDecision;
    const insuranceCrossCheckItems = [{
      label: "Valerie plan",
      value: opsCrossCheck.primaryPlanLabel
    }, {
      label: "Valerie subscriber ID",
      value: opsCrossCheck.primarySubscriberId
    }, {
      label: "In network",
      value: inNetworkDisplay
    }, {
      label: "Referral",
      value: referralRequirementDisplay
    }, {
      label: "Authorization",
      value: authorizationRequirementDisplay
    }];
    const insuranceIntakeDetails = coverageApproval?.metadata ? <div className="space-y-3 rounded-md border p-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "Expected",
        value: coverageApproval.metadata.expectedCoverageCount
      }, {
        label: "In Athena",
        value: coverageApproval.metadata.currentCoverages.length
      }]} /><AthenaCoverageEvidence metadata={coverageApproval.metadata} /></div> : coverageReadback ? <div className="space-y-3 text-sm"><DecisionMetadata items={[...insuranceCrossCheckItems, {
        label: "In Athena",
        value: coverageReadback.coverages.length
      }, {
        label: "Self pay",
        value: coverageReadback.primary.selfPay ? "Yes" : "No"
      }]} /><div className="space-y-1"><p className="font-medium">Currently in Athena</p>{coverageReadback.coverages.length === 0 ? <p>No coverage found.</p> : coverageReadback.coverages.map(_temp17)}</div></div> : followThroughEligible || assignmentStatus === "completed" ? <DecisionMetadata items={insuranceCrossCheckItems} /> : null;
    const steps = [{
      number: 1,
      stepKey: TPR_TASK_STEP_KEY.LOCATE_DOCUMENT,
      title: "Patient Chart",
      status: patientChartStatus,
      summary: "Copy and search by the document ID in Referral.",
      preview: [{
        label: "Patient",
        value: opsCrossCheck.patientName
      }, {
        label: "EHR ID",
        value: patientEhrId
      }],
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient",
          value: <CopyableContent text={`${opsCrossCheck.patientName} (DOB: ${opsCrossCheck.patientDateOfBirth})`} textClassName="text-xs" />
        }, {
          label: "EHR ID",
          value: <CopyableContent text={patientEhrId} textClassName="text-xs" />
        }, {
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }, {
          label: "Home phone",
          value: <CopyableContent text={opsCrossCheck.patientHomePhoneNumber} textClassName="text-xs" />
        }, {
          label: "Mobile phone",
          value: <CopyableContent text={opsCrossCheck.patientMobilePhoneNumber} textClassName="text-xs" />
        }, {
          label: "Work phone",
          value: <CopyableContent text={opsCrossCheck.patientWorkPhoneNumber} textClassName="text-xs" />
        }, {
          label: "Email",
          value: <CopyableContent text={opsCrossCheck.patientEmail} textClassName="text-xs" />
        }, {
          label: "Fax",
          value: <CopyableContent text={opsCrossCheck.patientFaxNumber} textClassName="text-xs" />
        }, {
          label: "Ethnicity",
          value: opsCrossCheck.patientEthnicity
        }, {
          label: "Race",
          value: opsCrossCheck.patientRace
        }, {
          label: "Language",
          value: opsCrossCheck.patientLanguage
        }]} />{patientDisplayId ? <Link href={`/patients/${patientDisplayId}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground">Open patient details<SquareArrowOutUpRight className="size-3.5" /></Link> : null}</div>
    }, {
      number: 2,
      stepKey: TPR_TASK_STEP_KEY.PROVIDER_LOCATION,
      title: "Receiving Provider and Location",
      status: assignmentConfirmationStatus,
      workflowStepKeys: [StepKey.RESOLVE_RECEIVING_ASSIGNMENT, StepKey.TPR_CAMELBACK_FOLLOW_THROUGH],
      preview: [{
        label: "Patient type",
        value: patientLabel
      }, {
        label: "Provider",
        value: resolvedReceiving.providerLabel
      }, {
        label: "Location",
        value: resolvedReceiving.locationLabel
      }],
      summary: usesRetiredUsualProviderRule ? "This legacy assignment used Athena's usual provider and must be checked against qualifying appointment history." : assignmentNeedsReview ? "Provider and location require manual review before downstream referral work." : "Verify we selected the correct receiving provider and location.",
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Patient type",
          value: patientLabel
        }, {
          label: "Provider",
          value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "NPI",
          value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Location",
          value: <CopyableContent text={resolvedReceiving.locationLabel || NOT_AVAILABLE} textClassName="text-xs" />
        }, {
          label: "Resolution",
          value: usesRetiredUsualProviderRule ? "Verify appointment history" : assignmentRequiresManualReview ? "Manual review" : "Confirmed"
        }]} />{providerAssignmentEditable ? <TprReceivingAssignmentEditor task={task} currentProviderNpi={resolvedReceiving.providerNpi || null} currentDepartmentId={resolvedReceiving.locationEhrId || null} /> : null}<ProviderSelectionChecklist evidence={providerSelectionEvidence} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} referralProviderName={opsCrossCheck.receivingProviderName} referralProviderNpi={opsCrossCheck.receivingProviderNpi} diagnosis={opsCrossCheck.diagnosis} diagnosisCategories={opsCrossCheck.diagnosisCategories} selectedProviderName={resolvedReceiving.providerLabel || NOT_AVAILABLE} selectedLocation={resolvedReceiving.locationLabel || NOT_AVAILABLE} />{resolvedReceiving.advisories.length > 0 ? <div className="space-y-1 rounded-md border border-warn/30 bg-warn/5 p-3"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</p><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.advisories.map(_temp18)}</ul></div> : null}{assignmentNeedsReview && resolvedReceiving.flags.length > 0 ? <><ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{resolvedReceiving.flags.map(_temp19)}</ul>{unresolvedUpcomingProviders.length > 0 ? <div className="flex flex-wrap items-center gap-2 rounded-md border border-warn/30 bg-warn/5 p-3"><span className="text-xs font-medium text-muted-foreground">Upcoming appointment provider</span>{unresolvedUpcomingProviders.map(_temp20)}</div> : null}</> : null}</div>,
      continuation: receivingAssignmentApproval ? {
        instruction: "After confirming the provider and location in Athena, continue the workflow.",
        control: <TprReceivingAssignmentContinueButton task={task} />
      } : undefined
    }, {
      number: 3,
      stepKey: TPR_TASK_STEP_KEY.DEMOGRAPHICS_CARE_TEAM,
      title: "Demographics and Care Team",
      preview: [{
        label: "PCP",
        value: pcpDisposition
      }],
      status: demographicsStatus,
      phase: hasEmergencyContactToEnter ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: hasEmergencyContactToEnter ? "Automation can't add the emergency contact. Enter it in Athena, then mark this updated." : undefined,
      workflowStepKeys: [StepKey.ENSURE_PATIENT, StepKey.SYNC_PATIENT_APPOINTMENTS, StepKey.UPDATE_CARE_TEAM],
      summary: hasEmergencyContactToEnter ? "Add the emergency contact to Athena by hand, then review patient and care team details in Quickview." : "Review patient details, care team details, and emergency contact separately in Quickview.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Patient Details</p><DecisionMetadata items={[{
            label: "Patient",
            value: opsCrossCheck.patientName
          }, {
            label: "DOB",
            value: opsCrossCheck.patientDateOfBirth
          }, {
            label: "Home phone",
            value: opsCrossCheck.patientHomePhoneNumber
          }, {
            label: "Mobile phone",
            value: opsCrossCheck.patientMobilePhoneNumber
          }, {
            label: "Work phone",
            value: opsCrossCheck.patientWorkPhoneNumber
          }, {
            label: "Email",
            value: opsCrossCheck.patientEmail
          }, {
            label: "Home address",
            value: opsCrossCheck.patientHomeAddress
          }]} /></div><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Care Team</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Usual Provider</p><DecisionMetadata items={resolvedReceiving.usualProviderWriteSafe ? [{
              label: "Provider",
              value: <CopyableContent text={resolvedReceiving.providerLabel || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={resolvedReceiving.providerNpi || NOT_AVAILABLE} textClassName="text-xs" />
            }, {
              label: "Result",
              value: "Updated for new patient"
            }] : [{
              label: "Result",
              value: "Left unchanged for established patient"
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }, {
              label: "Specialty",
              value: opsCrossCheck.referringProviderSpecialty
            }, {
              label: "Added as PCP",
              value: pcpDisposition
            }]} /></div></div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Emergency Contact</p><DecisionMetadata items={[{
            label: "Name",
            value: <CopyableContent text={opsCrossCheck.emergencyContactName} textClassName="text-xs" />
          }, {
            label: "Relationship",
            value: <CopyableContent text={opsCrossCheck.emergencyContactRelationship} textClassName="text-xs" />
          }, {
            label: "Phone",
            value: <CopyableContent text={opsCrossCheck.emergencyContactPhoneNumber} textClassName="text-xs" />
          }]} /></div>{demographicsReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-foreground">{demographicsReviewReasons.map(_temp21)}</ul> : null}</div>
    }, {
      number: 4,
      stepKey: TPR_TASK_STEP_KEY.INSURANCE,
      title: "Insurance",
      preview: [{
        label: "Plan",
        value: opsCrossCheck.primaryPlanLabel
      }, {
        label: "In network",
        value: insuranceRequirements ? inNetworkDisplay : null
      }],
      status: eligibilityTimedOut ? "review_needed" : insuranceStatus,
      phase: coverageApproval || eligibilityTimedOut ? TaskStepPhase.ACTION_NEEDED : undefined,
      workflowStepKeys: [StepKey.CHECK_INSURANCE_ELIGIBILITY, StepKey.CREATE_COVERAGE, StepKey.CONFIRM_ATHENA_COVERAGE, StepKey.READ_ATHENA_COVERAGE, StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY, StepKey.SYNC_ATHENA_COVERAGES, StepKey.RESOLVE_TPR_INSURANCE_REQUIREMENTS],
      summary: coverageApproval ? coverageApproval.metadata?.instruction ?? "Confirm the Athena coverage before continuing." : coverageReadback?.primary.selfPay ? "Self Pay \u2014 no network or eligibility checks apply." : insuranceRequirements ? `In network: ${inNetworkDisplay}. Athena eligibility: ${insuranceStatusDisplay}.` : coverageReadbackComplete ? "Coverage confirmed. Waiting on Athena eligibility and network/requirements decisions." : assignmentRequiresManualReview ? "Not run because the receiving provider and location require manual confirmation." : "Waiting for the coverage write, Athena eligibility, and network/requirements decisions.",
      content: <div className="space-y-3">{athenaCoverageDrifted ? <p className="rounded-md border border-warn/40 bg-warn/10 p-3 text-sm text-foreground">Athena's primary insurance has changed since this referral synced. The network and eligibility results below were decided against the coverage we wrote, not the one on the chart now — re-check them before relying on them.</p> : null}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">{opsCrossCheck.primaryCoverageSlot.title}</p><DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.primaryPlanLabel} textClassName="text-xs" />{opsCrossCheck.isSpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.primarySubscriberId} textClassName="text-xs" />
          }, {
            label: "Policy holder",
            value: <CopyableContent text={opsCrossCheck.primaryPolicyHolder} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={livePrimaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: primaryCoverageInAthena ? "Yes" : "No"
          }, {
            label: "In network",
            value: coverageReadback?.primary.selfPay ? "N/A \u2014 Self Pay" : inNetworkDisplay
          }]} />{coverageReadback && insuranceRequirements ? <TprNetworkStatus task={task} steps={taskSteps} coverage={coverageReadback} requirements={insuranceRequirements} /> : null}<DecisionMetadata items={[{
            label: "Coverage eligibility",
            value: followThroughEligible ? insuranceStatusDisplay : skippedFollowThroughSummary
          }]} />{followThroughEligible && insuranceStatusDisplay === "Unverified" && !coverageReadback?.primary.selfPay ? <p className="text-sm">Coverage eligibility still needs verification.</p> : null}</div><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">{opsCrossCheck.secondaryCoverageSlot.title}</p>{opsCrossCheck.secondaryPlanLabel || coverageReadback?.secondary || athenaCoverageLive.secondary ? <DecisionMetadata items={[{
            label: "Plan (from Valerie)",
            value: <span className="inline-flex items-center gap-1.5"><CopyableContent text={opsCrossCheck.secondaryPlanLabel || NOT_AVAILABLE} textClassName="text-xs" />{opsCrossCheck.isSecondarySpecialCaseInsurance ? <Badge variant="outline" className="border-warn/40 bg-warn/10 text-warn">Special Case Insurance</Badge> : null}</span>
          }, {
            label: "Subscriber ID (from Valerie)",
            value: <CopyableContent text={opsCrossCheck.secondarySubscriberId || NOT_AVAILABLE} textClassName="text-xs" />
          }, {
            label: "Policy holder",
            value: <CopyableContent text={opsCrossCheck.secondaryPolicyHolder || NOT_AVAILABLE} textClassName="text-xs" />
          }, {
            label: "Plan (in Athena, live)",
            value: <CopyableContent text={liveSecondaryAthenaPlan} textClassName="text-xs" />
          }, {
            label: "Synced to Athena",
            value: athenaCoverageLive.secondary || coverageReadback?.secondary ? "Yes" : "No"
          }]} /> : <p className="text-sm text-muted-foreground">No secondary insurance was captured from the referral or found in Athena.</p>}</div><details className="text-xs"><summary className="cursor-pointer text-muted-foreground">More insurance detail</summary><div className="mt-3 space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Coverage</p><DecisionMetadata items={[{
                label: "Self pay",
                value: coverageReadback?.primary.selfPay ? "Yes" : "No"
              }]} /><TprStediEligibilityValues referralId={referralId} />{insuranceIntakeDetails ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">Show Athena coverage detail</summary><div className="mt-2">{insuranceIntakeDetails}</div></details> : null}</div>{insuranceRequirements?.reviewReasons.length ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Network Review Reasons</p><ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">{insuranceRequirements.reviewReasons.map(_temp22)}</ul></div> : null}</div></details></div>,
      continuation: coverageApproval ? {
        instruction: "Choose each policy from Valerie or Athena, including its subscriber details, then sync to continue.",
        control: <AthenaCoverageResolutionDialog taskId={task.id} />
      } : eligibilityTimedOut ? {
        instruction: "Athena did not return a complete eligibility response in time. Review coverage in Athena, then recheck eligibility.",
        control: <TprManualStepContinueButton task={task} stepKey={StepKey.SUBMIT_ATHENA_INSURANCE_ELIGIBILITY} />
      } : undefined
    }, {
      number: 5,
      stepKey: TPR_TASK_STEP_KEY.REFERRAL_AUTHORIZATION,
      title: "Referral Authorization",
      status: resolveConsolidatedStatus([pcpFaxStatus, authorizationStatus]),
      phase: referralAuthNeedsOutboundFax ? TaskStepPhase.ACTION_NEEDED : undefined,
      detail: referralAuthNeedsOutboundFax ? "This authorization needs an outbound fax, which isn't automated. Send it from Athena, then continue." : undefined,
      workflowStepKeys: [StepKey.TPR_PCP_REFERRAL_FAX, StepKey.TPR_REFERRAL_AUTHORIZATION],
      summary: opsKind === "pcp_referral_fax" ? opsApproval?.metadata?.instruction ?? "This plan requires the referral to come from the PCP. Export the chart and send the outbound fax." : opsKind === "authorization_review" ? opsApproval?.metadata?.instruction ?? "Review the Athena referral authorization object, then continue." : opsKind === "manual_authorization" ? opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION : referralAuthorization?.outcome === "created" ? `${referralAuthorization.write === "authorization" ? "Authorization" : "Paper referral"} object ${referralAuthorization.alreadyExisted ? "already existed" : "was created"} in Athena${referralAuthorization.pending ? " with number pending" : ""}.` : referralAuthorization?.outcome === "needs_pending_request" ? "No authorization number is on file. An outbound request letter is required next." : tprReferralAuthNeedsReview(referralAuthorization, insuranceRequirements) ? "No Athena object was created because the authorization requirements need manual review." : referralAuthorization?.outcome === "not_needed" ? `No Athena object was needed (${formatFromUnderscoreToHumanReadable(referralAuthorization.reason)}).` : followThroughEligible ? "Waiting for network, authorization, and referral-requirement decisions." : skippedFollowThroughSummary,
      content: <div className="space-y-3">{insuranceRequirements ? null : <p className="text-sm text-muted-foreground">{followThroughEligible ? "Waiting for network/requirements decision." : skippedFollowThroughSummary}</p>}{insuranceRequirements && !showPcpReferralRequirement && !showReferralRequirement ? <p className="text-sm text-muted-foreground">This plan does not require a referral or authorization.</p> : null}{showPcpReferralRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">PCP Referral Requirement</p><DecisionMetadata items={[{
            label: "PCP referral",
            value: "Required"
          }, {
            label: "Referring is PCP",
            value: isPrimaryCareProvider ? "Yes" : "No"
          }]} /></div> : null}{showReferralRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Referral Requirement</p><DecisionMetadata items={[{
            label: "Referral",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements?.referralRequirement ?? NOT_AVAILABLE)
          }]} />{showAuthorizationRequirement ? null : authorizationDetails}</div> : null}{showAuthorizationRequirement ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Requirement</p><DecisionMetadata items={[{
            label: "Authorization",
            value: formatFromUnderscoreToHumanReadable(insuranceRequirements?.authorizationRequirement ?? NOT_AVAILABLE)
          }]} />{authorizationDetails}</div> : null}{insuranceRequirements ? null : <TprSyncAuthButtons canUploadLetter={referralAuthNeedsPendingUpload} enabled={Boolean(task.referral && patientEhrId !== NOT_AVAILABLE)} taskId={task.id} />}{pcpFaxStatus === "review_needed" || authorizationStatus === "review_needed" ? <details className="text-xs"><summary className="text-muted-foreground">How to complete this manually</summary><div className="mt-2 space-y-3"><div><p className="font-medium text-foreground">Referral Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>If a referral is required, confirm the received referral automatically created an object under the Authorization section.</li><li>If it did not, manually create the referral object with:<ul className="list-disc space-y-1 pl-5"><li>Insurance / authorization number: always "paper"</li><li>Referral dates — default (if not provided): today through 1 year later</li><li>Number of visits — default (if not provided): 12 visits</li><li>ICD-10 codes</li><li>Referring provider</li><li>Specialty</li><li>Only if listed on the referral doc: receiving provider and department</li></ul></li></ul></div><div><p className="font-medium text-foreground">Referral Authorization Required</p><ul className="list-disc space-y-1 pl-5 text-muted-foreground"><li>Same workflow as above, but enter the authorization number in the Insurance / authorization number field.</li><li>Only send a fax if there is no authorization number. When sending an authorization request for a missing auth number:<ul className="list-disc space-y-1 pl-5"><li>Manually export the doc to the referring provider via AthenaFax.</li><li>Add an authorization object with "Pending" in the auth number field.</li><li>In all notes (patient notes, document internal note, action note): "Referral Authorization Requested [Date]"</li></ul></li></ul></div></div></details> : null}</div>,
      continuation: opsKind === "pcp_referral_fax" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After sending the outbound PCP referral fax from Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="PCP referral fax confirmed" successDescription="The workflow is continuing with authorization." />
      } : opsKind === "authorization_review" ? {
        instruction: opsApproval?.metadata?.instruction ?? "After reviewing the Athena referral authorization object, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Authorization reviewed" successDescription="The workflow is continuing with the remaining referral steps." />
      } : opsKind === "manual_authorization" ? {
        instruction: opsApproval?.metadata?.instruction ?? TPR_MANUAL_REFERRAL_AUTH_DIRECTORY_INSTRUCTION,
        control: <TprOpsContinueButton task={task} successTitle="Manual authorization recorded" successDescription="The workflow is continuing with the remaining referral steps." />
      } : undefined
    }, {
      number: 6,
      stepKey: TPR_TASK_STEP_KEY.OUTBOUND_FAXES,
      detail: "Sending these faxes is still partly manual. Send them from Athena, then continue.",
      title: "Outbound Faxes",
      status: outboundFaxStatus,
      workflowStepKeys: [StepKey.TPR_REFERRAL_AUTH_REQUEST_CONFIRM, StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "auth_request_upload" ? "Export the patient chart in Athena manually, then upload the request letter. The letter is not uploaded automatically." : outboundFaxStatus === "not_needed" ? "No outbound fax is required for this referral." : "Send the required outbound fax(es) from Athena, then upload where applicable.",
      content: <div className="space-y-3">{referralAuthNeedsPendingUpload || authRequestUploaded ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Authorization Request Letter</p><DecisionMetadata items={[{
            label: "Request letter",
            value: authRequestUploaded ? "Uploaded" : "Waiting for ops"
          }]} />{outboundFaxStatus === "review_needed" ? <ol className="list-decimal space-y-1 pl-5 text-sm"><li>Export the patient chart in Athena. Do not skip this export.</li><li>Click Continue below to upload the request letter. The letter is not uploaded automatically.</li><li>Athena then receives a Referral Auth object with number pending.</li></ol> : null}</div> : null}{hasMissingInformation ? <div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Missing Patient Phone Packet</p><DecisionMetadata items={[{
            label: "Missing-info packet",
            value: missingInfoUploadComplete ? "Sent to Athena chart" : "Waiting for automation"
          }]} /><p className="text-sm text-muted-foreground">Generated automatically when the patient phone number is missing — no upload needed here. Once it appears in the Athena chart, send it from Athena like any other outbound fax.</p></div> : null}{outboundFaxStatus === "not_needed" ? <p className="text-sm text-muted-foreground">Nothing to send — the referral authorization doesn't need an outbound request, and the patient phone number isn't missing.</p> : null}</div>,
      continuation: opsKind === "auth_request_upload" ? {
        instruction: "After exporting the chart in Athena, click Continue to upload the request letter.",
        control: <TprOpsContinueButton task={task} successTitle="Request letter queued" successDescription="The workflow will write the pending authorization and upload the letter." />
      } : undefined
    }, {
      number: 7,
      stepKey: TPR_TASK_STEP_KEY.FULL_REGISTRATION,
      title: "Full Registration",
      status: fullRegistrationStatus,
      summary: "Open Full Registration and verify communication consent, privacy, and referral source.",
      workflowStepKeys: [StepKey.ENSURE_PATIENT],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Calls",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Texts",
          value: "Set to Yes only if blank \u2014 existing value is not overridden"
        }, {
          label: "Privacy Notice",
          value: privacyNoticeOnFile === null ? "Not read back" : privacyNoticeOnFile ? "Selected" : "Not selected"
        }, {
          label: "How did you hear about us",
          value: "Set to Physician Referral only if blank \u2014 existing value is not overridden"
        }]} />{fullRegistrationReviewReasons.length ? <ul className="list-disc space-y-1 pl-5 text-sm text-warn-foreground">{fullRegistrationReviewReasons.map(_temp23)}</ul> : null}</div>
    }, {
      number: 8,
      stepKey: TPR_TASK_STEP_KEY.PROCESS_REFERRAL_DOCUMENT,
      detail: "Parts of the referral document have to be keyed into Athena by hand. Enter them, then mark this updated.",
      title: "Referral Document",
      status: taskStepStatus(taskSteps, [StepKey.REFERRAL_DOCUMENT_METADATA]),
      summary: "Referral metadata is written to Athena in the order ops should verify it.",
      workflowStepKeys: [StepKey.REFERRAL_DOCUMENT_METADATA],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Document ID",
          value: <CopyableContent text={documentId} textClassName="text-xs" />
        }]} /><div className="space-y-3 rounded-md border p-3"><p className="text-sm font-medium">Referral Fields</p><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">1. Referring Provider</p><DecisionMetadata items={[{
              label: "Name",
              value: <CopyableContent text={opsCrossCheck.referringProviderName} textClassName="text-xs" />
            }, {
              label: "NPI",
              value: <CopyableContent text={opsCrossCheck.referringProviderNpi} textClassName="text-xs" />
            }, {
              label: "Address",
              value: <CopyableContent text={opsCrossCheck.referringLocationAddress} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">2. New or Established Patient</p><DecisionMetadata items={[{
              label: "Patient type",
              value: <CopyableContent text={patientLabel} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">3. Date Received</p><DecisionMetadata items={[{
              label: "Date received",
              value: <CopyableContent text={opsCrossCheck.dateReceived} textClassName="text-xs" />
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">4. Urgency</p><DecisionMetadata items={[{
              label: "Urgent",
              value: urgency.isUrgent ? "Yes" : "No"
            }, ...(urgency.reason ? [{
              label: "Reason",
              value: urgency.reason
            }] : []), {
              label: "Scheduling priority",
              value: urgency.schedulingPriority || NOT_AVAILABLE
            }]} /></div><div className="space-y-1"><p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">5. Internal Note</p><DecisionMetadata items={[{
              label: "Internal Note",
              value: <CopyableContent text={internalNoteToEnter} textClassName="text-xs" />
            }]} /></div></div></div>
    }, {
      number: 9,
      stepKey: TPR_TASK_STEP_KEY.DOCUMENT_UPLOAD,
      title: "Document Upload",
      status: taskStepStatus(taskSteps, [StepKey.SUPPORTING_SECTIONS_UPLOAD]),
      summary: documentUploadLabels.length ? "Confirm the expected documents were uploaded to Athena." : "No documents were selected for upload.",
      workflowStepKeys: [StepKey.SUPPORTING_SECTIONS_UPLOAD],
      content: <div className="space-y-3"><DecisionMetadata items={[{
          label: "Documents",
          value: documentUploadLabels.length
        }]} /><div className="space-y-1"><p className="text-sm font-medium">Expected Athena uploads</p>{documentUploadLabels.length ? <ul className="list-disc space-y-1 pl-5 text-sm">{documentUploadLabels.map(_temp24)}</ul> : <p className="text-sm text-muted-foreground">No documents were selected for upload.</p>}</div></div>
    }, {
      number: 10,
      stepKey: TPR_TASK_STEP_KEY.QUICKVIEW,
      title: "Quickview Note",
      status: notesComplete ? "verification_required" : dispositionStatus,
      workflowStepKeys: [],
      summary: notesComplete ? "Confirm the note above appears correctly on the patient's Quickview tab in Athena." : "Waiting for the final note to be written before this can be verified.",
      content: notesComplete ? <p className="text-sm">Confirm the final note appears correctly on the patient's Quickview tab in Athena.</p> : undefined
    }, {
      number: 11,
      stepKey: TPR_TASK_STEP_KEY.BUCKET_ASSIGNMENT,
      detail: "Choosing the bucket is ops' call. Assign it in Athena, then mark this updated.",
      title: "Bucket Assignment",
      status: resolveConsolidatedStatus([closeDocumentStatus, dispositionStatus === "failed" ? "not_run" : !workflowStillProcessing && notesComplete && (!hasMissingInformation || missingInfoUploadComplete) ? "review_needed" : taskStepStatus(taskSteps, [StepKey.REFERRAL_DISPOSITION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX])]),
      workflowStepKeys: [StepKey.TPR_CLOSE_DOCUMENT, StepKey.REFERRAL_DISPOSITION, StepKey.SCHEDULING_RECOMMENDATION, StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX],
      summary: opsKind === "close_document" ? opsApproval?.metadata?.instruction ?? "There is an upcoming appointment within 90 days. Close the document in Athena, then continue." : hasMissingInformation && !missingInfoUploadComplete ? "Waiting for the missing-information document to be prepared." : referralAuthNeedsPendingUpload && authRequestUploaded ? "Confirm the authorization-request letter on the chart, route, and complete." : hasMissingInformation ? "Send the missing-information document, route the referral, and complete only after all manual actions are done." : "Confirm the recommended destination bucket \u2014 SS Cardio, Biltmore, or Close \u2014 and complete the task.",
      content: <div className="space-y-3"><div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Bucket</p><DecisionMetadata items={[{
            label: "Suggested bucket",
            value: <CopyableContent text={bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? NO_CONFIDENT_BUCKET_SUGGESTION : SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} />
          }, {
            label: "Missing information",
            value: hasMissingInformation ? "Required" : "No"
          }, {
            label: "Authorization request",
            value: authRequestUploaded ? "Uploaded" : "Not required"
          }]} />{decisionTrace.length > 0 ? <details className="text-xs"><summary className="cursor-pointer text-muted-foreground">How this was determined</summary><ul className="mt-2 list-disc space-y-1 pl-5 text-muted-foreground">{decisionTrace.map(_temp25)}</ul></details> : null}</div>{!resolvedReceiving.routedToSscardio && !workflowStillProcessing && notesComplete && task.type === TaskType.REFERRAL && task.referral && <TaskSchedulingRecommendation taskId={task.id} referral={task.referral} preferAgenticReceivingProvider={true} />}<div className="space-y-2 rounded-md border p-3"><p className="text-sm font-medium">Action Notes</p><DecisionMetadata items={[{
            label: "Action Notes",
            value: <CopyableContent text={actionNote} textClassName="text-xs" />
          }]} /></div></div>,
      continuation: opsKind === "close_document" ? {
        instruction: "After closing the document in Athena, continue the workflow.",
        control: <TprOpsContinueButton task={task} successTitle="Document close confirmed" successDescription="The workflow is continuing with remaining follow-through." />
      } : undefined
    }];
    const failedTaskStepFor = card => taskSteps.filter(row => row.status === TaskStepStatus.FAILED && card.workflowStepKeys.includes(row.stepKey as StepKey)).toSorted(_temp26)[0];
    const failureReasonOf = _temp27;
    const stoppedOnFailureCard = steps.find(card_0 => {
      const row_1 = failedTaskStepFor(card_0);
      return row_1 !== undefined && !isTprContinuedFailure(task.status, taskSteps, row_1);
    });
    let sequenceOpen = true;
    let sequenceBlockedBy = null;
    const phasedSteps = steps.map(step_6 => {
      const offersManualBypass = Boolean(manualBypassStepKey && step_6.workflowStepKeys.includes(manualBypassStepKey));
      const pinnedPhase = offersManualBypass ? TaskStepPhase.ACTION_NEEDED : step_6.phase ?? TPR_PINNED_STEP_PHASE[step_6.stepKey];
      const failedTaskStep = failedTaskStepFor(step_6);
      const isFailedSync = failedTaskStep !== undefined;
      const continuedPastFailure = failedTaskStep !== undefined && isTprContinuedFailure(task.status, taskSteps, failedTaskStep);
      const status_0 = isFailedSync ? "failed" : offersManualBypass ? "review_needed" : step_6.status;
      const naturalPhase = resolveTaskStepPhase(status_0, pinnedPhase);
      const verdict = verdictByStepKey.get(step_6.stepKey)?.verdict;
      const ownsPendingGate = step_6.continuation !== undefined || pendingApproval !== null && pendingApproval.stepKey !== null && step_6.workflowStepKeys.includes(pendingApproval.stepKey);
      const confirmed = !isFailedSync && !ownsPendingGate && !offersManualBypass && verdict === VerificationResult.CORRECT;
      const signOffOnContinue = continuedPastFailure && verdict !== VerificationResult.INCORRECT && step_6.workflowStepKeys.every(stepKey => taskSteps.some(row_2 => row_2.stepKey === stepKey && (row_2.status === TaskStepStatus.COMPLETED || row_2 === failedTaskStep)));
      const presented = sequenceOpen || isFailedSync || ownsPendingGate || offersManualBypass;
      const phase = presented ? naturalPhase : null;
      const awaitingStep = presented ? null : sequenceBlockedBy;
      if (sequenceOpen && !continuedPastFailure && step_6.status !== "not_needed" && !(naturalPhase !== null && confirmed)) {
        sequenceOpen = false;
        sequenceBlockedBy = step_6;
      }
      return {
        step: {
          ...step_6,
          status: status_0,
          phase: pinnedPhase
        },
        failedStepKey: failedTaskStep?.stepKey as StepKey | undefined ?? null,
        failureReason: failedTaskStep ? failureReasonOf(failedTaskStep) : null,
        continuedPastFailure,
        signOffOnContinue,
        phase,
        awaitingStep,
        naturalPhase
      };
    });
    const timelineNotice = waitingForAssociatedReferral ? "Waiting for a matching Referral for this patient. Standalone Referral Auth holds until a Referral in the two-day window finishes syncing." : phasedSteps.some(_temp28) ? null : workflowStillProcessing ? "Automation is still running. This timeline will update as each durable workflow step completes." : pendingApproval ? "This referral is held at an approval no step can resolve. It needs engineering \u2014 escalate rather than closing it out." : "No manual actions remain.";
    let t5;
    if ($[97] !== timelineNotice) {
      t5 = timelineNotice ? <p className="text-sm text-muted-foreground">{timelineNotice}</p> : null;
      $[97] = timelineNotice;
      $[98] = t5;
    } else {
      t5 = $[98];
    }
    t4 = <div className="space-y-8 pb-8">{t5}{phasedSteps.map(t6 => {
        const {
          step: step_7,
          naturalPhase: naturalPhase_0,
          awaitingStep: awaitingStep_0,
          failedStepKey,
          failureReason,
          continuedPastFailure: continuedPastFailure_0,
          signOffOnContinue: signOffOnContinue_0
        } = t6;
        return <TimelineStepCard key={step_7.number} task={task} step={step_7} failedStepKey={failedStepKey} bypassStepKey={manualBypassStepKey && step_7.workflowStepKeys.includes(manualBypassStepKey) ? manualBypassStepKey : null} failureReason={failureReason} continuedPastFailure={continuedPastFailure_0} signOffOnContinue={signOffOnContinue_0} blockedBy={naturalPhase_0 === null && stoppedOnFailureCard && stoppedOnFailureCard.number !== step_7.number ? stoppedOnFailureCard : null} awaitingStep={awaitingStep_0} />;
      })}</div>;
    $[8] = actionNote;
    $[9] = athenaCoverageLive.primary;
    $[10] = athenaCoverageLive.secondary;
    $[11] = bucketLabel;
    $[12] = chartReferralAuths;
    $[13] = chartRequestLetters;
    $[14] = coverageReadback;
    $[15] = decisionTrace;
    $[16] = documentId;
    $[17] = documentUploadLabels;
    $[18] = followThroughEligible;
    $[19] = hasMissingInformation;
    $[20] = insuranceRequirements;
    $[21] = insuranceStatusDisplay;
    $[22] = internalNoteToEnter;
    $[23] = isPrimaryCareProvider;
    $[24] = isProviderFirst;
    $[25] = opsCrossCheck.authorizationEndDate;
    $[26] = opsCrossCheck.authorizationNumber;
    $[27] = opsCrossCheck.authorizationServices;
    $[28] = opsCrossCheck.authorizationStartDate;
    $[29] = opsCrossCheck.authorizationStatus;
    $[30] = opsCrossCheck.authorizationVisits;
    $[31] = opsCrossCheck.dateReceived;
    $[32] = opsCrossCheck.diagnosis;
    $[33] = opsCrossCheck.diagnosisCategories;
    $[34] = opsCrossCheck.emergencyContactName;
    $[35] = opsCrossCheck.emergencyContactPhoneNumber;
    $[36] = opsCrossCheck.emergencyContactRelationship;
    $[37] = opsCrossCheck.isSecondarySpecialCaseInsurance;
    $[38] = opsCrossCheck.isSpecialCaseInsurance;
    $[39] = opsCrossCheck.patientDateOfBirth;
    $[40] = opsCrossCheck.patientEmail;
    $[41] = opsCrossCheck.patientEthnicity;
    $[42] = opsCrossCheck.patientFaxNumber;
    $[43] = opsCrossCheck.patientHomeAddress;
    $[44] = opsCrossCheck.patientHomePhoneNumber;
    $[45] = opsCrossCheck.patientLanguage;
    $[46] = opsCrossCheck.patientMobilePhoneNumber;
    $[47] = opsCrossCheck.patientName;
    $[48] = opsCrossCheck.patientRace;
    $[49] = opsCrossCheck.patientWorkPhoneNumber;
    $[50] = opsCrossCheck.primaryCoverageSlot.title;
    $[51] = opsCrossCheck.primaryPlanLabel;
    $[52] = opsCrossCheck.primaryPolicyHolder;
    $[53] = opsCrossCheck.primarySubscriberId;
    $[54] = opsCrossCheck.receivingProviderName;
    $[55] = opsCrossCheck.receivingProviderNpi;
    $[56] = opsCrossCheck.referringLocationAddress;
    $[57] = opsCrossCheck.referringProviderName;
    $[58] = opsCrossCheck.referringProviderNpi;
    $[59] = opsCrossCheck.referringProviderSpecialty;
    $[60] = opsCrossCheck.secondaryCoverageSlot.title;
    $[61] = opsCrossCheck.secondaryPlanLabel;
    $[62] = opsCrossCheck.secondaryPolicyHolder;
    $[63] = opsCrossCheck.secondarySubscriberId;
    $[64] = patientContactSyncGaps;
    $[65] = patientDisplayId;
    $[66] = patientEhrId;
    $[67] = patientLabel;
    $[68] = pendingApproval;
    $[69] = primaryCareProviderAlreadyPresent;
    $[70] = privacyNoticeOnFile;
    $[71] = providerAssignmentMarkedIncorrect;
    $[72] = providerSelectionEvidence;
    $[73] = receivingAssignmentConfirmed;
    $[74] = referralAuthorization;
    $[75] = referralId;
    $[76] = referralSourceOnFile;
    $[77] = referringProviderMarkedPcp;
    $[78] = resolvedReceiving.advisories;
    $[79] = resolvedReceiving.flags;
    $[80] = resolvedReceiving.locationEhrId;
    $[81] = resolvedReceiving.locationLabel;
    $[82] = resolvedReceiving.providerLabel;
    $[83] = resolvedReceiving.providerNpi;
    $[84] = resolvedReceiving.providerWriteSafe;
    $[85] = resolvedReceiving.routedToSscardio;
    $[86] = resolvedReceiving.usualProviderWriteSafe;
    $[87] = task;
    $[88] = taskSteps;
    $[89] = unresolvedUpcomingProviders;
    $[90] = upcomingAppointmentProviderSummary;
    $[91] = urgency.isUrgent;
    $[92] = urgency.reason;
    $[93] = urgency.schedulingPriority;
    $[94] = verdictByStepKey;
    $[95] = workflowStillProcessing;
    $[96] = t4;
  } else {
    t4 = $[96];
  }
  return t4;
}
function _temp28(entry_0) {
  return entry_0.phase !== null;
}
function _temp27(row_0) {
  return row_0.output?.error || row_0.output?.failureReason || row_0.output?.ccErrors ? errorFromStepOutput(row_0.output, row_0.stepKey).message : null;
}
function _temp26(left, right) {
  return left.stepIndex - right.stepIndex;
}
function _temp25(entry, index) {
  return <li key={`${entry.label}-${index}`}>{entry.label} <strong>{entry.outcome}</strong></li>;
}
function _temp24(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp23(reason_4) {
  return <li key={reason_4}>{reason_4}</li>;
}
function _temp22(reason_3) {
  return <li key={reason_3}>{reason_3}</li>;
}
function _temp21(reason_2) {
  return <li key={reason_2}>{reason_2}</li>;
}
function _temp20(provider) {
  return <Badge key={provider.providerEhrId ?? provider.label} variant="outline">{provider.label}{provider.providerEhrId ? ` · Athena ID ${provider.providerEhrId}` : ""}</Badge>;
}
function _temp19(reason_1) {
  return <li key={reason_1}>{reason_1}</li>;
}
function _temp18(advisory) {
  return <li key={advisory}>{advisory}</li>;
}
function _temp17(coverage) {
  return <p key={coverage.insuranceId}><strong>{coverage.casePolicy ? "Case policy" : coverage.sequence === 1 ? "Primary" : coverage.sequence === 2 ? "Secondary" : `Sequence ${coverage.sequence ?? "N/A"}`}:</strong>{" "}{coverage.planName ?? "Unknown plan"} · Package <CopyableContent text={coverage.insurancePackageId} />{coverage.subscriberId ? <>{" "}· Subscriber <CopyableContent text={coverage.subscriberId} /></> : null}</p>;
}
function _temp16(reason_0) {
  return reason_0 !== null;
}
function _temp15(reason) {
  return reason !== null;
}
function _temp14(file) {
  return file.type === FileType.INBOUND;
}
function _temp13(letter) {
  return <li key={letter.documentEhrId}><CopyableContent text={letter.documentEhrId} textClassName="text-xs" />{letter.documentDate ? ` · ${letter.documentDate}` : ""}{letter.description ? ` · ${letter.description}` : ""}</li>;
}
function _temp12(authorization_0) {
  return <li key={authorization_0.referralAuthId}><CopyableContent text={authorization_0.referralAuthId} textClassName="text-xs" />{authorization_0.number ? ` · ${authorization_0.number}` : ""}{authorization_0.startDate || authorization_0.endDate ? ` · ${authorization_0.startDate ?? "\u2014"}–${authorization_0.endDate ?? "\u2014"}` : ""}{authorization_0.expired ? " \xB7 expired" : ""}</li>;
}
function _temp11(status) {
  return status === "required" || status === "manual_review";
}
function _temp10(statuses) {
  if (statuses.includes("failed")) {
    return "failed";
  }
  if (statuses.includes("review_needed")) {
    return "review_needed";
  }
  if (statuses.includes("verification_required")) {
    return "verification_required";
  }
  if (statuses.includes("waiting")) {
    return "waiting";
  }
  if (statuses.includes("completed")) {
    return "completed";
  }
  if (statuses.includes("not_run")) {
    return "not_run";
  }
  return "not_needed";
}
function _temp9(step_5) {
  return step_5.stepKey === StepKey.ATHENA_UPLOAD_MISSING_INFO_FAX && step_5.status === TaskStepStatus.COMPLETED;
}
function _temp8(step_4) {
  return step_4.stepKey === StepKey.REFERRAL_DISPOSITION && step_4.status === TaskStepStatus.COMPLETED;
}
function _temp7(step_3) {
  return step_3.stepKey === StepKey.TPR_REFERRAL_AUTH_REQUEST_UPLOAD && step_3.status === TaskStepStatus.COMPLETED && step_3.output?.uploaded === true;
}
function _temp6(step_2) {
  return step_2.stepKey === StepKey.READ_ATHENA_COVERAGE && step_2.status === TaskStepStatus.COMPLETED;
}
function _temp5(step_1) {
  return step_1.stepKey === StepKey.TPR_WAIT_FOR_ASSOCIATED_REFERRAL && step_1.status === TaskStepStatus.PAUSED;
}
function _temp4(step) {
  return step.stepKey === StepKey.RESOLVE_RECEIVING_ASSIGNMENT;
}
interface TprReferralInstructionsProps {
  view: "information" | "steps";
  referral: Referral;
  task: Task;
  athenaData: AthenaReferralCardsData | null;
  patientEhrId: string;
  /** "New Patient Referral" / "Established Patient Referral" / "N/A" — see getAthenaReferralDocumentLabel. */
  patientLabel: string;
  upcomingAppointments: Appointment[];
  /** Past booked appointments (lookback window matching the new/established determination) for the
   * "Patient Type" sanity-check list — display-only, not the source of the New/Established label itself. */
  pastAppointments: Appointment[];
  documentId: string;
  isPrimaryCareProvider: boolean;
  referringProviderMarkedPcp: boolean;
  /** Whether the referring provider is already on the Athena care team as PRIMARY_CARE_PROVIDER —
   * suppresses the redundant "PCP" line in Step 3's care team card. See resolveTprCareTeamPolicy. */
  primaryCareProviderAlreadyPresent: boolean;
  /** One of TprSyncBucket's four values: "closed" | "sscardio" | "biltmorecardreferral" | "biltmorecardiosched" */
  bucketLabel: string;
  actionNote: string;
  /** Athena document priority (Urgent vs Normal) and why — shown in Step 8 before the internal note. */
  urgency: TprSyncChecklistUrgency;
  /** The note the disposition step will write, always carrying the
   * "Reviewed By Valerie Health (date) - <provider> - ..." prefix. Step 8 shows this rather than the
   * Athena read-back, which is still empty at that point in the workflow. */
  expectedInternalNote: string;
  /** Each decision-tree check that ran, in order, with its outcome — see resolveTprSyncChecklist. */
  decisionTrace: TprSyncChecklistTraceEntry[];
  /** Non-stopping context notes from the decision tree (insurance-sync issues, urgency reason, the
   * detailed "ops must manually verify..." explanation behind a short bucket-assignment label, etc). */
  flags: string[];
  /** "Eligible" | "Ineligible" | "Unverified" — same Athena-then-Stedi result the decision tree reads. */
  eligibilityDisplay: StediEligibilityDisplayLabel;
  documentUploadLabels: string[];
  privacyNoticeOnFile: boolean | null;
  /** Athena's current `referralsourceid`, or null when blank. */
  referralSourceOnFile: string | null | undefined;
  taskSteps: TaskStep[];
  pendingApproval: TaskApproval | null;
  receivingAssignmentConfirmed: boolean;
  followThroughEligible: boolean;
  coverageReadback: AthenaCoverageReadbackOutput | null;
  insuranceRequirements: TprInsuranceRequirementsOutput | null;
  referralAuthorization: TprReferralAuthorizationOutput | null;
  unresolvedUpcomingProviders: Array<{
    label: string;
    providerEhrId: string | null;
  }>;
  upcomingAppointmentProviderSummary: string | null;
  /** Persisted provider-first assignment, or the legacy display fallback for older tasks. */
  resolvedReceiving: {
    providerLabel: string | null;
    providerNpi: string | null;
    providerWriteSafe: boolean;
    usualProviderWriteSafe: boolean;
    locationLabel: string | null;
    /** Athena department id behind `locationLabel` — preselects the ops location picker. */
    locationEhrId: string | null;
    trace: string[];
    flags: string[];
    /** Non-blocking reviewer notices (e.g. the Terrace cardiology/neurology check) — always shown,
     * unlike `flags`, which only renders once the assignment already needs manual review. */
    advisories: string[];
    routedToSscardio: boolean;
    providerSelectionEvidence?: ReceivingAssignmentOutput["providerSelectionEvidence"];
  };
  chartReferralAuths: TprChartReferralAuth[];
  chartRequestLetters: TprChartRequestLetter[];
}
export function TprReferralInstructions(t0) {
  const $ = _c(489);
  const {
    view,
    referral,
    task,
    athenaData,
    patientEhrId,
    patientLabel,
    upcomingAppointments,
    pastAppointments,
    documentId,
    isPrimaryCareProvider,
    referringProviderMarkedPcp,
    primaryCareProviderAlreadyPresent,
    bucketLabel,
    actionNote,
    urgency,
    expectedInternalNote,
    decisionTrace,
    flags,
    eligibilityDisplay,
    documentUploadLabels,
    privacyNoticeOnFile,
    referralSourceOnFile,
    taskSteps,
    pendingApproval,
    receivingAssignmentConfirmed,
    followThroughEligible,
    coverageReadback,
    insuranceRequirements,
    referralAuthorization,
    unresolvedUpcomingProviders,
    upcomingAppointmentProviderSummary,
    resolvedReceiving,
    chartReferralAuths,
    chartRequestLetters
  } = t0;
  let t1;
  if ($[0] !== referral.referringLocation?.address) {
    t1 = getFormattedAddress(referral.referringLocation?.address) || NOT_AVAILABLE;
    $[0] = referral.referringLocation?.address;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const referringLocationAddress = t1;
  const referringProviderSpecialty = referral.referringProvider?.specialty || NOT_AVAILABLE;
  let t2;
  if ($[2] !== referral.referringProvider) {
    t2 = referral.referringProvider ? getPractitionerNameWithCredentials(referral.referringProvider) : NOT_AVAILABLE;
    $[2] = referral.referringProvider;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const referringProviderName = t2;
  let t3;
  if ($[4] !== referral.receivingProvider) {
    t3 = referral.receivingProvider ? getPractitionerNameWithCredentials(referral.receivingProvider) : NOT_AVAILABLE;
    $[4] = referral.receivingProvider;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const receivingProviderName = t3;
  const patient = referral.patient;
  const emergencyContactName = patient?.emergencyContactName || NOT_AVAILABLE;
  const emergencyContactPhoneNumber = patient?.emergencyContactPhoneNumber || NOT_AVAILABLE;
  const emergencyContactRelationship = patient?.emergencyContactRelationshipToPatient || NOT_AVAILABLE;
  let t4;
  if ($[6] !== referral.primaryCoverage) {
    t4 = getCoverageSummary(referral.primaryCoverage);
    $[6] = referral.primaryCoverage;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const primaryCoverageSummary = t4;
  let t5;
  if ($[8] !== referral.secondaryCoverage) {
    t5 = getCoverageSummary(referral.secondaryCoverage);
    $[8] = referral.secondaryCoverage;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const secondaryCoverageSummary = t5;
  let t6;
  if ($[10] !== referral.primaryCoverage || $[11] !== referral.secondaryCoverage) {
    t6 = getTprCoverageSlots(referral.primaryCoverage, referral.secondaryCoverage);
    $[10] = referral.primaryCoverage;
    $[11] = referral.secondaryCoverage;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const coverageSlots = t6;
  let t7;
  if ($[13] !== referral.dateReceived) {
    t7 = formatDateMmDdYyyy(referral.dateReceived) || NOT_AVAILABLE;
    $[13] = referral.dateReceived;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const dateReceived = t7;
  const schedulingPriority = referral.schedulingPriority || NOT_AVAILABLE;
  const athenaInternalNote = athenaData?.document?.internalNote || NOT_AVAILABLE;
  const internalNoteToEnter = athenaInternalNote.toLowerCase().includes(REVIEWED_BY_VALERIE_HEALTH_MARKER) ? athenaInternalNote : expectedInternalNote;
  let T0;
  let dataSourceEntries;
  let hasMissingInformation;
  let missingFields;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  let t22;
  let t23;
  let t24;
  let t25;
  let t26;
  let t8;
  let t9;
  if ($[15] !== actionNote || $[16] !== athenaData?.coverages || $[17] !== athenaData?.patientDetails || $[18] !== bucketLabel || $[19] !== chartReferralAuths || $[20] !== chartRequestLetters || $[21] !== coverageReadback || $[22] !== coverageSlots.primary || $[23] !== coverageSlots.secondary || $[24] !== dateReceived || $[25] !== decisionTrace || $[26] !== documentId || $[27] !== documentUploadLabels || $[28] !== eligibilityDisplay || $[29] !== emergencyContactName || $[30] !== emergencyContactPhoneNumber || $[31] !== emergencyContactRelationship || $[32] !== followThroughEligible || $[33] !== insuranceRequirements || $[34] !== internalNoteToEnter || $[35] !== isPrimaryCareProvider || $[36] !== pastAppointments || $[37] !== patient || $[38] !== patientEhrId || $[39] !== patientLabel || $[40] !== pendingApproval || $[41] !== primaryCareProviderAlreadyPresent || $[42] !== primaryCoverageSummary || $[43] !== privacyNoticeOnFile || $[44] !== receivingAssignmentConfirmed || $[45] !== receivingProviderName || $[46] !== referral || $[47] !== referralAuthorization || $[48] !== referralSourceOnFile || $[49] !== referringLocationAddress || $[50] !== referringProviderMarkedPcp || $[51] !== referringProviderName || $[52] !== referringProviderSpecialty || $[53] !== resolvedReceiving || $[54] !== secondaryCoverageSummary || $[55] !== task || $[56] !== taskSteps || $[57] !== unresolvedUpcomingProviders || $[58] !== upcomingAppointmentProviderSummary || $[59] !== upcomingAppointments || $[60] !== urgency || $[61] !== view) {
    t26 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const sortedPastAppointments = sortAppointmentsMostRecentFirst(pastAppointments);
      const syncFailed = task.status === TaskStatus.FAILED;
      const insuranceStatusDisplay = coverageReadback?.primary.selfPay ? "Self Pay" : referral.primaryCoverage ? eligibilityDisplay : "Missing Insurance";
      const chartPatient = athenaData?.patientDetails ?? null;
      let t27;
      if ($[85] !== patient) {
        t27 = patient && getPatientPhoneNumber(patient)?.trim() || null;
        $[85] = patient;
        $[86] = t27;
      } else {
        t27 = $[86];
      }
      const referralPhone = t27;
      let t28;
      if ($[87] !== chartPatient) {
        t28 = chartPatient && getPatientPhoneNumber(chartPatient)?.trim() || null;
        $[87] = chartPatient;
        $[88] = t28;
      } else {
        t28 = $[88];
      }
      const chartPhone = t28;
      let t29;
      if ($[89] !== chartPatient?.homePhoneNumber || $[90] !== patient?.homePhoneNumber) {
        t29 = patient?.homePhoneNumber?.trim() || chartPatient?.homePhoneNumber?.trim() || null;
        $[89] = chartPatient?.homePhoneNumber;
        $[90] = patient?.homePhoneNumber;
        $[91] = t29;
      } else {
        t29 = $[91];
      }
      const homePhone = t29;
      let t30;
      if ($[92] !== chartPatient?.mobilePhoneNumber || $[93] !== patient?.mobilePhoneNumber) {
        t30 = patient?.mobilePhoneNumber?.trim() || chartPatient?.mobilePhoneNumber?.trim() || null;
        $[92] = chartPatient?.mobilePhoneNumber;
        $[93] = patient?.mobilePhoneNumber;
        $[94] = t30;
      } else {
        t30 = $[94];
      }
      const mobilePhone = t30;
      let t31;
      if ($[95] !== chartPatient?.workPhoneNumber || $[96] !== patient?.workPhoneNumber) {
        t31 = patient?.workPhoneNumber?.trim() || chartPatient?.workPhoneNumber?.trim() || null;
        $[95] = chartPatient?.workPhoneNumber;
        $[96] = patient?.workPhoneNumber;
        $[97] = t31;
      } else {
        t31 = $[97];
      }
      const workPhone = t31;
      let t32;
      if ($[98] !== chartPatient?.personalEmail || $[99] !== patient?.personalEmail) {
        t32 = patient?.personalEmail?.trim().toLowerCase() || chartPatient?.personalEmail?.trim().toLowerCase() || null;
        $[98] = chartPatient?.personalEmail;
        $[99] = patient?.personalEmail;
        $[100] = t32;
      } else {
        t32 = $[100];
      }
      const email = t32;
      let patientContactSyncGaps;
      let phoneDataSourceEntry;
      let syncedAddressValue;
      let t33;
      let t34;
      let t35;
      if ($[101] !== athenaData?.coverages || $[102] !== chartPatient || $[103] !== chartPhone || $[104] !== patient || $[105] !== referral.primaryCoverage || $[106] !== referralPhone) {
        const referralCoverageValue = getCoverageSummary(referral.primaryCoverage);
        const chartPrimaryCoverage = [...(athenaData?.coverages ?? [])].sort(_temp29)[0];
        const chartCoverageValue = getCoverageSummary(chartPrimaryCoverage ?? null);
        const referralAddressValue = patient?.homeAddress ? getFormattedAddress(patient.homeAddress) : null;
        const chartAddressValue = chartPatient?.homeAddress ? getFormattedAddress(chartPatient.homeAddress) : null;
        syncedAddressValue = isCompleteAddress(patient?.homeAddress) ? referralAddressValue : chartAddressValue ?? referralAddressValue;
        let t36;
        if ($[113] !== chartPatient || $[114] !== patient) {
          t36 = patient && chartPatient ? findPatientContactSyncGaps(patient, chartPatient) : [];
          $[113] = chartPatient;
          $[114] = patient;
          $[115] = t36;
        } else {
          t36 = $[115];
        }
        patientContactSyncGaps = t36;
        let t37;
        if ($[116] !== chartPhone || $[117] !== referralPhone) {
          t37 = resolveDataSourceEntry("Phone Number", referralPhone, chartPhone);
          $[116] = chartPhone;
          $[117] = referralPhone;
          $[118] = t37;
        } else {
          t37 = $[118];
        }
        phoneDataSourceEntry = t37;
        t33 = phoneDataSourceEntry;
        t34 = resolveDataSourceEntry("Insurance", referralCoverageValue, chartCoverageValue);
        t35 = resolveDataSourceEntry("Home Address", referralAddressValue, chartAddressValue);
        $[101] = athenaData?.coverages;
        $[102] = chartPatient;
        $[103] = chartPhone;
        $[104] = patient;
        $[105] = referral.primaryCoverage;
        $[106] = referralPhone;
        $[107] = patientContactSyncGaps;
        $[108] = phoneDataSourceEntry;
        $[109] = syncedAddressValue;
        $[110] = t33;
        $[111] = t34;
        $[112] = t35;
      } else {
        patientContactSyncGaps = $[107];
        phoneDataSourceEntry = $[108];
        syncedAddressValue = $[109];
        t33 = $[110];
        t34 = $[111];
        t35 = $[112];
      }
      let t36;
      if ($[119] !== t33 || $[120] !== t34 || $[121] !== t35) {
        t36 = [t33, t34, t35];
        $[119] = t33;
        $[120] = t34;
        $[121] = t35;
        $[122] = t36;
      } else {
        t36 = $[122];
      }
      dataSourceEntries = t36;
      const isCamelbackReferral = !resolvedReceiving.routedToSscardio;
      let t37;
      if ($[123] !== isCamelbackReferral || $[124] !== phoneDataSourceEntry) {
        t37 = isCamelbackReferral && phoneDataSourceEntry.missing ? [phoneDataSourceEntry] : [];
        $[123] = isCamelbackReferral;
        $[124] = phoneDataSourceEntry;
        $[125] = t37;
      } else {
        t37 = $[125];
      }
      missingFields = t37;
      hasMissingInformation = missingFields.length > 0;
      let t38;
      if ($[126] !== patient) {
        t38 = getPatientDisplayName(patient);
        $[126] = patient;
        $[127] = t38;
      } else {
        t38 = $[127];
      }
      const patientDisplayName = t38;
      const receivingLocationName = referral.receivingLocation?.name || NOT_AVAILABLE;
      let t39;
      if ($[128] !== referral.diagnosisList || $[129] !== referral.reasonForReferral) {
        t39 = [referral.reasonForReferral, ...((referral.diagnosisList ?? []) as DiagnosisItem[]).map(_temp30)].filter(_temp31).filter(_temp32).join(", ") || NOT_AVAILABLE;
        $[128] = referral.diagnosisList;
        $[129] = referral.reasonForReferral;
        $[130] = t39;
      } else {
        t39 = $[130];
      }
      const diagnosis = t39;
      let t40;
      if ($[131] !== referral) {
        t40 = describeTprSpecialtyCategories(matchTprDiagnosisSpecialties(referral)) || "None matched";
        $[131] = referral;
        $[132] = t40;
      } else {
        t40 = $[132];
      }
      const diagnosisCategories = t40;
      let t41;
      if ($[133] !== patient) {
        t41 = patient ? getPatientName(patient) || NOT_AVAILABLE : NOT_AVAILABLE;
        $[133] = patient;
        $[134] = t41;
      } else {
        t41 = $[134];
      }
      let t42;
      if ($[135] !== patient?.dateOfBirth) {
        t42 = formatDateMmDdYyyy(patient?.dateOfBirth) || NOT_AVAILABLE;
        $[135] = patient?.dateOfBirth;
        $[136] = t42;
      } else {
        t42 = $[136];
      }
      const t43 = homePhone ?? NOT_AVAILABLE;
      const t44 = mobilePhone ?? NOT_AVAILABLE;
      const t45 = workPhone ?? NOT_AVAILABLE;
      let t46;
      if ($[137] !== patient) {
        t46 = patient && getPatientFaxNumber(patient) || NOT_AVAILABLE;
        $[137] = patient;
        $[138] = t46;
      } else {
        t46 = $[138];
      }
      const t47 = patient?.ethnicity || NOT_AVAILABLE;
      const t48 = patient?.race || NOT_AVAILABLE;
      const t49 = patient?.language || NOT_AVAILABLE;
      const t50 = email ?? NOT_AVAILABLE;
      const t51 = syncedAddressValue || NOT_AVAILABLE;
      const t52 = referral.receivingProvider?.npiNumber || NOT_AVAILABLE;
      const t53 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      const t54 = referral.referringLocation?.name || NOT_AVAILABLE;
      let t55;
      if ($[139] !== referral.primaryCoverage) {
        t55 = referral.primaryCoverage ? getCoverageDisplayName({
          coverage: referral.primaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : "Self Pay";
        $[139] = referral.primaryCoverage;
        $[140] = t55;
      } else {
        t55 = $[140];
      }
      const t56 = referral.primaryCoverage?.subscriberId || NOT_AVAILABLE;
      let t57;
      if ($[141] !== referral.primaryCoverage) {
        t57 = getPolicyHolder(referral.primaryCoverage) || NOT_AVAILABLE;
        $[141] = referral.primaryCoverage;
        $[142] = t57;
      } else {
        t57 = $[142];
      }
      const t58 = referral.primaryCoverage?.payer;
      let t59;
      if ($[143] !== t58) {
        t59 = isTprSpecialCaseInsurance(t58);
        $[143] = t58;
        $[144] = t59;
      } else {
        t59 = $[144];
      }
      const t60 = coverageSlots.primary;
      const t61 = coverageSlots.secondary;
      let t62;
      if ($[145] !== referral.secondaryCoverage) {
        t62 = referral.secondaryCoverage ? getCoverageDisplayName({
          coverage: referral.secondaryCoverage,
          includesPlan: true,
          includeSubscriberName: false
        }) : null;
        $[145] = referral.secondaryCoverage;
        $[146] = t62;
      } else {
        t62 = $[146];
      }
      const t63 = referral.secondaryCoverage?.subscriberId || null;
      let t64;
      if ($[147] !== referral.secondaryCoverage) {
        t64 = getPolicyHolder(referral.secondaryCoverage);
        $[147] = referral.secondaryCoverage;
        $[148] = t64;
      } else {
        t64 = $[148];
      }
      const t65 = referral.secondaryCoverage?.payer;
      let t66;
      if ($[149] !== t65) {
        t66 = isTprSpecialCaseInsurance(t65);
        $[149] = t65;
        $[150] = t66;
      } else {
        t66 = $[150];
      }
      let t67;
      if ($[151] !== referral.authorizationNumber) {
        t67 = referral.authorizationNumber?.trim() || NOT_AVAILABLE;
        $[151] = referral.authorizationNumber;
        $[152] = t67;
      } else {
        t67 = $[152];
      }
      let t68;
      if ($[153] !== referral.authorizationStatus) {
        t68 = referral.authorizationStatus ? formatFromUnderscoreToHumanReadable(referral.authorizationStatus) : NOT_AVAILABLE;
        $[153] = referral.authorizationStatus;
        $[154] = t68;
      } else {
        t68 = $[154];
      }
      let t69;
      if ($[155] !== referral.startDate) {
        t69 = formatDateMmDdYyyy(referral.startDate) || NOT_AVAILABLE;
        $[155] = referral.startDate;
        $[156] = t69;
      } else {
        t69 = $[156];
      }
      let t70;
      if ($[157] !== referral.endDate) {
        t70 = formatDateMmDdYyyy(referral.endDate) || NOT_AVAILABLE;
        $[157] = referral.endDate;
        $[158] = t70;
      } else {
        t70 = $[158];
      }
      let t71;
      if ($[159] !== referral.numberOfVisits) {
        t71 = referral.numberOfVisits?.toString() ?? NOT_AVAILABLE;
        $[159] = referral.numberOfVisits;
        $[160] = t71;
      } else {
        t71 = $[160];
      }
      let t72;
      if ($[161] !== referral.numberOfServices) {
        t72 = referral.numberOfServices?.toString() ?? null;
        $[161] = referral.numberOfServices;
        $[162] = t72;
      } else {
        t72 = $[162];
      }
      let t73;
      if ($[163] !== coverageSlots.primary || $[164] !== coverageSlots.secondary || $[165] !== dateReceived || $[166] !== diagnosis || $[167] !== diagnosisCategories || $[168] !== emergencyContactName || $[169] !== emergencyContactPhoneNumber || $[170] !== emergencyContactRelationship || $[171] !== receivingProviderName || $[172] !== referringLocationAddress || $[173] !== referringProviderName || $[174] !== referringProviderSpecialty || $[175] !== t41 || $[176] !== t42 || $[177] !== t43 || $[178] !== t44 || $[179] !== t45 || $[180] !== t46 || $[181] !== t47 || $[182] !== t48 || $[183] !== t49 || $[184] !== t50 || $[185] !== t51 || $[186] !== t52 || $[187] !== t53 || $[188] !== t54 || $[189] !== t55 || $[190] !== t56 || $[191] !== t57 || $[192] !== t59 || $[193] !== t62 || $[194] !== t63 || $[195] !== t64 || $[196] !== t66 || $[197] !== t67 || $[198] !== t68 || $[199] !== t69 || $[200] !== t70 || $[201] !== t71 || $[202] !== t72) {
        t73 = {
          patientName: t41,
          patientDateOfBirth: t42,
          patientHomePhoneNumber: t43,
          patientMobilePhoneNumber: t44,
          patientWorkPhoneNumber: t45,
          patientFaxNumber: t46,
          patientEthnicity: t47,
          patientRace: t48,
          patientLanguage: t49,
          patientEmail: t50,
          patientHomeAddress: t51,
          receivingProviderName,
          receivingProviderNpi: t52,
          referringProviderName,
          referringProviderNpi: t53,
          referringProviderSpecialty,
          referringLocationName: t54,
          referringLocationAddress,
          emergencyContactName,
          emergencyContactPhoneNumber,
          emergencyContactRelationship,
          dateReceived,
          primaryPlanLabel: t55,
          primarySubscriberId: t56,
          primaryPolicyHolder: t57,
          isSpecialCaseInsurance: t59,
          primaryCoverageSlot: t60,
          secondaryCoverageSlot: t61,
          secondaryPlanLabel: t62,
          secondarySubscriberId: t63,
          secondaryPolicyHolder: t64,
          isSecondarySpecialCaseInsurance: t66,
          authorizationNumber: t67,
          authorizationStatus: t68,
          authorizationStartDate: t69,
          authorizationEndDate: t70,
          authorizationVisits: t71,
          authorizationServices: t72,
          diagnosis,
          diagnosisCategories
        };
        $[163] = coverageSlots.primary;
        $[164] = coverageSlots.secondary;
        $[165] = dateReceived;
        $[166] = diagnosis;
        $[167] = diagnosisCategories;
        $[168] = emergencyContactName;
        $[169] = emergencyContactPhoneNumber;
        $[170] = emergencyContactRelationship;
        $[171] = receivingProviderName;
        $[172] = referringLocationAddress;
        $[173] = referringProviderName;
        $[174] = referringProviderSpecialty;
        $[175] = t41;
        $[176] = t42;
        $[177] = t43;
        $[178] = t44;
        $[179] = t45;
        $[180] = t46;
        $[181] = t47;
        $[182] = t48;
        $[183] = t49;
        $[184] = t50;
        $[185] = t51;
        $[186] = t52;
        $[187] = t53;
        $[188] = t54;
        $[189] = t55;
        $[190] = t56;
        $[191] = t57;
        $[192] = t59;
        $[193] = t62;
        $[194] = t63;
        $[195] = t64;
        $[196] = t66;
        $[197] = t67;
        $[198] = t68;
        $[199] = t69;
        $[200] = t70;
        $[201] = t71;
        $[202] = t72;
        $[203] = t73;
      } else {
        t73 = $[203];
      }
      const opsCrossCheck = t73;
      let t74;
      let t75;
      if ($[204] !== athenaData?.coverages) {
        const sortedAthenaCoverages = [...(athenaData?.coverages ?? [])].sort(_temp33);
        t74 = sortedAthenaCoverages.find(_temp34) ?? sortedAthenaCoverages[0] ?? null;
        t75 = sortedAthenaCoverages.find(_temp35) ?? sortedAthenaCoverages[1] ?? null;
        $[204] = athenaData?.coverages;
        $[205] = t74;
        $[206] = t75;
      } else {
        t74 = $[205];
        t75 = $[206];
      }
      let t76;
      if ($[207] !== t74 || $[208] !== t75) {
        t76 = {
          primary: t74,
          secondary: t75
        };
        $[207] = t74;
        $[208] = t75;
        $[209] = t76;
      } else {
        t76 = $[209];
      }
      const athenaCoverageLive = t76;
      if (view === "steps") {
        let t77;
        if ($[210] !== actionNote || $[211] !== athenaCoverageLive || $[212] !== bucketLabel || $[213] !== chartReferralAuths || $[214] !== chartRequestLetters || $[215] !== coverageReadback || $[216] !== decisionTrace || $[217] !== documentId || $[218] !== documentUploadLabels || $[219] !== followThroughEligible || $[220] !== hasMissingInformation || $[221] !== insuranceRequirements || $[222] !== insuranceStatusDisplay || $[223] !== internalNoteToEnter || $[224] !== isPrimaryCareProvider || $[225] !== opsCrossCheck || $[226] !== pastAppointments || $[227] !== patient.displayId || $[228] !== patientContactSyncGaps || $[229] !== patientEhrId || $[230] !== patientLabel || $[231] !== pendingApproval || $[232] !== primaryCareProviderAlreadyPresent || $[233] !== privacyNoticeOnFile || $[234] !== receivingAssignmentConfirmed || $[235] !== referral.id || $[236] !== referralAuthorization || $[237] !== referralSourceOnFile || $[238] !== referringProviderMarkedPcp || $[239] !== resolvedReceiving || $[240] !== task || $[241] !== taskSteps || $[242] !== unresolvedUpcomingProviders || $[243] !== upcomingAppointmentProviderSummary || $[244] !== upcomingAppointments || $[245] !== urgency) {
          t77 = <div className="flex w-full max-w-2xl flex-col gap-8 sm:min-w-[800px] mx-auto"><TprSyncTimeline task={task} referralId={referral.id} taskSteps={taskSteps} pendingApproval={pendingApproval} receivingAssignmentConfirmed={receivingAssignmentConfirmed} followThroughEligible={followThroughEligible} coverageReadback={coverageReadback} athenaCoverageLive={athenaCoverageLive} insuranceRequirements={insuranceRequirements} referralAuthorization={referralAuthorization} resolvedReceiving={resolvedReceiving} patientLabel={patientLabel} patientEhrId={patientEhrId} documentId={documentId} bucketLabel={bucketLabel} internalNoteToEnter={internalNoteToEnter} urgency={urgency} actionNote={actionNote} decisionTrace={decisionTrace} insuranceStatusDisplay={insuranceStatusDisplay} hasMissingInformation={hasMissingInformation} unresolvedUpcomingProviders={unresolvedUpcomingProviders} upcomingAppointmentProviderSummary={upcomingAppointmentProviderSummary} upcomingAppointments={upcomingAppointments} pastAppointments={pastAppointments} documentUploadLabels={documentUploadLabels} privacyNoticeOnFile={privacyNoticeOnFile} referralSourceOnFile={referralSourceOnFile} isPrimaryCareProvider={isPrimaryCareProvider} referringProviderMarkedPcp={referringProviderMarkedPcp} primaryCareProviderAlreadyPresent={primaryCareProviderAlreadyPresent} patientDisplayId={patient.displayId} opsCrossCheck={opsCrossCheck} patientContactSyncGaps={patientContactSyncGaps} chartReferralAuths={chartReferralAuths} chartRequestLetters={chartRequestLetters} /></div>;
          $[210] = actionNote;
          $[211] = athenaCoverageLive;
          $[212] = bucketLabel;
          $[213] = chartReferralAuths;
          $[214] = chartRequestLetters;
          $[215] = coverageReadback;
          $[216] = decisionTrace;
          $[217] = documentId;
          $[218] = documentUploadLabels;
          $[219] = followThroughEligible;
          $[220] = hasMissingInformation;
          $[221] = insuranceRequirements;
          $[222] = insuranceStatusDisplay;
          $[223] = internalNoteToEnter;
          $[224] = isPrimaryCareProvider;
          $[225] = opsCrossCheck;
          $[226] = pastAppointments;
          $[227] = patient.displayId;
          $[228] = patientContactSyncGaps;
          $[229] = patientEhrId;
          $[230] = patientLabel;
          $[231] = pendingApproval;
          $[232] = primaryCareProviderAlreadyPresent;
          $[233] = privacyNoticeOnFile;
          $[234] = receivingAssignmentConfirmed;
          $[235] = referral.id;
          $[236] = referralAuthorization;
          $[237] = referralSourceOnFile;
          $[238] = referringProviderMarkedPcp;
          $[239] = resolvedReceiving;
          $[240] = task;
          $[241] = taskSteps;
          $[242] = unresolvedUpcomingProviders;
          $[243] = upcomingAppointmentProviderSummary;
          $[244] = upcomingAppointments;
          $[245] = urgency;
          $[246] = t77;
        } else {
          t77 = $[246];
        }
        t26 = t77;
        break bb0;
      }
      t25 = "flex flex-col gap-8 sm:min-w-[800px] w-full max-w-2xl mx-auto";
      T0 = EhrInstructionsCard;
      t24 = "Detailed Athena verification reference";
      t17 = "text-sm text-muted-foreground space-y-6";
      if ($[247] === Symbol.for("react.memo_cache_sentinel")) {
        t18 = <h3 className="text-md font-semibold text-center text-foreground">TPR Biltmore Cardiology Sync Verification</h3>;
        $[247] = t18;
      } else {
        t18 = $[247];
      }
      const t77 = syncFailed ? "Automation did not complete for this task. Resolve the sync error below and verify Athena before closing." : "Automation has already run for this task. Use this checklist to verify the sync in Athena before closing.";
      if ($[248] !== t77) {
        t19 = <p className="text-xs text-center">{t77}</p>;
        $[248] = t77;
        $[249] = t19;
      } else {
        t19 = $[249];
      }
      if ($[250] !== task.id) {
        t20 = <SyncIncompleteActions taskId={task.id} />;
        $[250] = task.id;
        $[251] = t20;
      } else {
        t20 = $[251];
      }
      let t78;
      if ($[252] === Symbol.for("react.memo_cache_sentinel")) {
        t78 = <strong>Camelback Referral:</strong>;
        $[252] = t78;
      } else {
        t78 = $[252];
      }
      const t79 = isCamelbackReferral ? "Yes" : "No";
      let t80;
      if ($[253] !== t79) {
        t80 = <p>{t78} {t79}</p>;
        $[253] = t79;
        $[254] = t80;
      } else {
        t80 = $[254];
      }
      let t81;
      if ($[255] === Symbol.for("react.memo_cache_sentinel")) {
        t81 = <strong>Patient:</strong>;
        $[255] = t81;
      } else {
        t81 = $[255];
      }
      let t82;
      if ($[256] !== patientDisplayName) {
        t82 = <CopyableContent text={patientDisplayName} />;
        $[256] = patientDisplayName;
        $[257] = t82;
      } else {
        t82 = $[257];
      }
      let t83;
      if ($[258] !== patientLabel || $[259] !== t82) {
        t83 = <p>{t81} {t82} ({patientLabel})</p>;
        $[258] = patientLabel;
        $[259] = t82;
        $[260] = t83;
      } else {
        t83 = $[260];
      }
      let t84;
      if ($[261] === Symbol.for("react.memo_cache_sentinel")) {
        t84 = <strong>Receiving Provider (as extracted):</strong>;
        $[261] = t84;
      } else {
        t84 = $[261];
      }
      let t85;
      if ($[262] !== referral.receivingProvider) {
        t85 = <ProviderCopyFields provider={referral.receivingProvider} />;
        $[262] = referral.receivingProvider;
        $[263] = t85;
      } else {
        t85 = $[263];
      }
      let t86;
      if ($[264] === Symbol.for("react.memo_cache_sentinel")) {
        t86 = <strong>Location:</strong>;
        $[264] = t86;
      } else {
        t86 = $[264];
      }
      let t87;
      if ($[265] !== receivingLocationName) {
        t87 = <CopyableContent text={receivingLocationName} />;
        $[265] = receivingLocationName;
        $[266] = t87;
      } else {
        t87 = $[266];
      }
      let t88;
      if ($[267] !== t85 || $[268] !== t87) {
        t88 = <p>{t84}{" "}{t85}, {t86}{" "}{t87}</p>;
        $[267] = t85;
        $[268] = t87;
        $[269] = t88;
      } else {
        t88 = $[269];
      }
      if ($[270] !== t80 || $[271] !== t83 || $[272] !== t88) {
        t21 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t80}{t83}{t88}</div>;
        $[270] = t80;
        $[271] = t83;
        $[272] = t88;
        $[273] = t21;
      } else {
        t21 = $[273];
      }
      let t89;
      let t90;
      if ($[274] === Symbol.for("react.memo_cache_sentinel")) {
        t89 = <p className="font-semibold">Recommended Receiving Provider / Location</p>;
        t90 = <strong>Provider:</strong>;
        $[274] = t89;
        $[275] = t90;
      } else {
        t89 = $[274];
        t90 = $[275];
      }
      const t91 = resolvedReceiving.providerLabel || NOT_AVAILABLE;
      let t92;
      if ($[276] !== t91) {
        t92 = <CopyableContent text={t91} />;
        $[276] = t91;
        $[277] = t92;
      } else {
        t92 = $[277];
      }
      let t93;
      if ($[278] === Symbol.for("react.memo_cache_sentinel")) {
        t93 = <strong>Location:</strong>;
        $[278] = t93;
      } else {
        t93 = $[278];
      }
      const t94 = resolvedReceiving.locationLabel || NOT_AVAILABLE;
      let t95;
      if ($[279] !== t94) {
        t95 = <CopyableContent text={t94} />;
        $[279] = t94;
        $[280] = t95;
      } else {
        t95 = $[280];
      }
      let t96;
      if ($[281] !== t92 || $[282] !== t95) {
        t96 = <p>{t90} {t92},{" "}{t93} {t95}</p>;
        $[281] = t92;
        $[282] = t95;
        $[283] = t96;
      } else {
        t96 = $[283];
      }
      let t97;
      if ($[284] !== resolvedReceiving.routedToSscardio) {
        t97 = resolvedReceiving.routedToSscardio && <p className="text-xs">Routing: <strong>sscardio</strong></p>;
        $[284] = resolvedReceiving.routedToSscardio;
        $[285] = t97;
      } else {
        t97 = $[285];
      }
      let t98;
      if ($[286] !== resolvedReceiving.flags) {
        t98 = resolvedReceiving.flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{resolvedReceiving.flags.map(_temp36)}</ul>;
        $[286] = resolvedReceiving.flags;
        $[287] = t98;
      } else {
        t98 = $[287];
      }
      let t99;
      if ($[288] !== resolvedReceiving.trace) {
        t99 = resolvedReceiving.trace.length > 0 && <details className="text-xs"><summary>Why</summary><ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{resolvedReceiving.trace.map(_temp37)}</ul></details>;
        $[288] = resolvedReceiving.trace;
        $[289] = t99;
      } else {
        t99 = $[289];
      }
      if ($[290] !== t96 || $[291] !== t97 || $[292] !== t98 || $[293] !== t99) {
        t22 = <div className="rounded-md border border-foreground/20 p-3 space-y-1 text-foreground">{t89}{t96}{t97}{t98}{t99}</div>;
        $[290] = t96;
        $[291] = t97;
        $[292] = t98;
        $[293] = t99;
        $[294] = t22;
      } else {
        t22 = $[294];
      }
      let t100;
      if ($[295] === Symbol.for("react.memo_cache_sentinel")) {
        t100 = <h4 className="font-semibold text-foreground mb-2">Patient Chart Create</h4>;
        $[295] = t100;
      } else {
        t100 = $[295];
      }
      let t101;
      if ($[296] !== documentId) {
        t101 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[296] = documentId;
        $[297] = t101;
      } else {
        t101 = $[297];
      }
      let t102;
      if ($[298] !== patientEhrId) {
        t102 = <li>Add patient EHR ID if not already added: <CopyableContent text={patientEhrId} /></li>;
        $[298] = patientEhrId;
        $[299] = t102;
      } else {
        t102 = $[299];
      }
      let t103;
      if ($[300] === Symbol.for("react.memo_cache_sentinel")) {
        t103 = <li>Hit save and proceed to quickview.</li>;
        $[300] = t103;
      } else {
        t103 = $[300];
      }
      let t104;
      if ($[301] !== resolvedReceiving.providerLabel || $[302] !== resolvedReceiving.providerNpi || $[303] !== resolvedReceiving.usualProviderWriteSafe) {
        t104 = resolvedReceiving.usualProviderWriteSafe ? <ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={true} /> : "Leave unchanged";
        $[301] = resolvedReceiving.providerLabel;
        $[302] = resolvedReceiving.providerNpi;
        $[303] = resolvedReceiving.usualProviderWriteSafe;
        $[304] = t104;
      } else {
        t104 = $[304];
      }
      const t105 = resolvedReceiving.usualProviderWriteSafe ? "New patient \u2014 add the usual provider to the care team." : "Established patient \u2014 left unchanged to preserve continuity of care.";
      let t106;
      if ($[305] !== t105) {
        t106 = <p className="text-xs italic mt-1">{t105}</p>;
        $[305] = t105;
        $[306] = t106;
      } else {
        t106 = $[306];
      }
      let t107;
      if ($[307] !== t104 || $[308] !== t106) {
        t107 = <li>Usual Provider:{" "}{t104}{t106}</li>;
        $[307] = t104;
        $[308] = t106;
        $[309] = t107;
      } else {
        t107 = $[309];
      }
      let t108;
      if ($[310] !== referral.referringProvider) {
        t108 = <ProviderCopyFields provider={referral.referringProvider} />;
        $[310] = referral.referringProvider;
        $[311] = t108;
      } else {
        t108 = $[311];
      }
      let t109;
      if ($[312] !== referringProviderSpecialty) {
        t109 = <CopyableContent text={referringProviderSpecialty} />;
        $[312] = referringProviderSpecialty;
        $[313] = t109;
      } else {
        t109 = $[313];
      }
      let t110;
      if ($[314] !== t108 || $[315] !== t109) {
        t110 = <li>Referring Provider: {t108}, Specialty:{" "}{t109}</li>;
        $[314] = t108;
        $[315] = t109;
        $[316] = t110;
      } else {
        t110 = $[316];
      }
      let t111;
      if ($[317] !== isPrimaryCareProvider || $[318] !== referral.referringProvider || $[319] !== referringProviderMarkedPcp) {
        t111 = (isPrimaryCareProvider || referringProviderMarkedPcp) && <li>PCP: <ProviderCopyFields provider={referral.referringProvider} /></li>;
        $[317] = isPrimaryCareProvider;
        $[318] = referral.referringProvider;
        $[319] = referringProviderMarkedPcp;
        $[320] = t111;
      } else {
        t111 = $[320];
      }
      let t112;
      if ($[321] !== referral.referringProvider || $[322] !== referringLocationAddress) {
        t112 = referral.referringProvider && <li>Referring provider address: <CopyableContent text={referringLocationAddress} /></li>;
        $[321] = referral.referringProvider;
        $[322] = referringLocationAddress;
        $[323] = t112;
      } else {
        t112 = $[323];
      }
      let t113;
      if ($[324] !== t107 || $[325] !== t110 || $[326] !== t111 || $[327] !== t112) {
        t113 = <li>Check the Patient Care Team:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t107}{t110}{t111}{t112}</ul></li>;
        $[324] = t107;
        $[325] = t110;
        $[326] = t111;
        $[327] = t112;
        $[328] = t113;
      } else {
        t113 = $[328];
      }
      let t114;
      if ($[329] !== emergencyContactName) {
        t114 = <li>Name: <CopyableContent text={emergencyContactName} /></li>;
        $[329] = emergencyContactName;
        $[330] = t114;
      } else {
        t114 = $[330];
      }
      let t115;
      if ($[331] !== emergencyContactPhoneNumber) {
        t115 = <li>Phone Number: <CopyableContent text={emergencyContactPhoneNumber} /></li>;
        $[331] = emergencyContactPhoneNumber;
        $[332] = t115;
      } else {
        t115 = $[332];
      }
      let t116;
      if ($[333] !== emergencyContactRelationship) {
        t116 = <li>Relationship to Patient: <CopyableContent text={emergencyContactRelationship} /></li>;
        $[333] = emergencyContactRelationship;
        $[334] = t116;
      } else {
        t116 = $[334];
      }
      let t117;
      if ($[335] !== t114 || $[336] !== t115 || $[337] !== t116) {
        t117 = <li>Check the Emergency Contact<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t114}{t115}{t116}</ul></li>;
        $[335] = t114;
        $[336] = t115;
        $[337] = t116;
        $[338] = t117;
      } else {
        t117 = $[338];
      }
      const t118 = primaryCoverageSummary ?? "N/A";
      let t119;
      if ($[339] !== t118) {
        t119 = <CopyableContent text={t118} />;
        $[339] = t118;
        $[340] = t119;
      } else {
        t119 = $[340];
      }
      let t120;
      if ($[341] !== coverageSlots.primary.label || $[342] !== t119) {
        t120 = <li>{coverageSlots.primary.label}: {t119}</li>;
        $[341] = coverageSlots.primary.label;
        $[342] = t119;
        $[343] = t120;
      } else {
        t120 = $[343];
      }
      let t121;
      if ($[344] !== coverageSlots.secondary || $[345] !== secondaryCoverageSummary) {
        t121 = secondaryCoverageSummary && <li>{coverageSlots.secondary.label}: <CopyableContent text={secondaryCoverageSummary} /></li>;
        $[344] = coverageSlots.secondary;
        $[345] = secondaryCoverageSummary;
        $[346] = t121;
      } else {
        t121 = $[346];
      }
      let t122;
      let t123;
      if ($[347] !== insuranceStatusDisplay) {
        t122 = <CopyableContent text={insuranceStatusDisplay} />;
        t123 = insuranceStatusDisplay === "Missing Insurance" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[347] = insuranceStatusDisplay;
        $[348] = t122;
        $[349] = t123;
      } else {
        t122 = $[348];
        t123 = $[349];
      }
      let t124;
      if ($[350] !== insuranceStatusDisplay) {
        t124 = insuranceStatusDisplay === "Unverified" && <p className="text-xs italic mt-1">Ops needs to call the referring provider to verify insurance.</p>;
        $[350] = insuranceStatusDisplay;
        $[351] = t124;
      } else {
        t124 = $[351];
      }
      let t125;
      if ($[352] !== t122 || $[353] !== t123 || $[354] !== t124) {
        t125 = <li>Eligibility check result: {t122}{t123}{t124}</li>;
        $[352] = t122;
        $[353] = t123;
        $[354] = t124;
        $[355] = t125;
      } else {
        t125 = $[355];
      }
      let t126;
      if ($[356] !== t120 || $[357] !== t121 || $[358] !== t125) {
        t126 = <li>Check the patient insurance<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t120}{t121}{t125}</ul></li>;
        $[356] = t120;
        $[357] = t121;
        $[358] = t125;
        $[359] = t126;
      } else {
        t126 = $[359];
      }
      let t127;
      if ($[360] === Symbol.for("react.memo_cache_sentinel")) {
        t127 = <li>Call and text are YES</li>;
        $[360] = t127;
      } else {
        t127 = $[360];
      }
      let t128;
      if ($[361] !== privacyNoticeOnFile) {
        t128 = privacyNoticeOnFile !== true && <p className="text-xs italic mt-1">{privacyNoticeOnFile === false ? "Privacy is not set on the Athena chart. Set it to YES during review." : "Could not verify Athena privacy. Confirm it is YES during review."}</p>;
        $[361] = privacyNoticeOnFile;
        $[362] = t128;
      } else {
        t128 = $[362];
      }
      let t129;
      if ($[363] !== t128) {
        t129 = <li>Privacy is YES{t128}</li>;
        $[363] = t128;
        $[364] = t129;
      } else {
        t129 = $[364];
      }
      let t130;
      if ($[365] === Symbol.for("react.memo_cache_sentinel")) {
        t130 = <li>If How did you hear about us is blank, set it to PHYSICIAN REFERRAL; leave any existing value unchanged.</li>;
        $[365] = t130;
      } else {
        t130 = $[365];
      }
      let t131;
      if ($[366] !== t129) {
        t131 = <li>Go to full registration and verify:<ul className="ml-4 mt-1 space-y-1 list-disc list-inside">{t127}{t129}{t130}</ul></li>;
        $[366] = t129;
        $[367] = t131;
      } else {
        t131 = $[367];
      }
      if ($[368] !== t101 || $[369] !== t102 || $[370] !== t113 || $[371] !== t117 || $[372] !== t126 || $[373] !== t131) {
        t23 = <div>{t100}<ol className="list-decimal list-inside space-y-3">{t101}{t102}{t103}{t113}{t117}{t126}{t131}</ol></div>;
        $[368] = t101;
        $[369] = t102;
        $[370] = t113;
        $[371] = t117;
        $[372] = t126;
        $[373] = t131;
        $[374] = t23;
      } else {
        t23 = $[374];
      }
      if ($[375] === Symbol.for("react.memo_cache_sentinel")) {
        t16 = <h4 className="font-semibold text-foreground mb-2">Referral Processing</h4>;
        $[375] = t16;
      } else {
        t16 = $[375];
      }
      t12 = "list-decimal list-inside space-y-3";
      if ($[376] !== documentId) {
        t13 = <li>Search by document ID: <CopyableContent text={documentId} /></li>;
        $[376] = documentId;
        $[377] = t13;
      } else {
        t13 = $[377];
      }
      let t132;
      if ($[378] !== referringProviderName) {
        t132 = <CopyableContent text={referringProviderName} />;
        $[378] = referringProviderName;
        $[379] = t132;
      } else {
        t132 = $[379];
      }
      const t133 = referral.referringProvider?.npiNumber || NOT_AVAILABLE;
      let t134;
      if ($[380] !== t133) {
        t134 = <CopyableContent text={t133} />;
        $[380] = t133;
        $[381] = t134;
      } else {
        t134 = $[381];
      }
      let t135;
      if ($[382] !== referringLocationAddress) {
        t135 = <CopyableContent text={referringLocationAddress} />;
        $[382] = referringLocationAddress;
        $[383] = t135;
      } else {
        t135 = $[383];
      }
      if ($[384] !== t132 || $[385] !== t134 || $[386] !== t135) {
        t14 = <li>Referring provider: Name: {t132}, NPI:{" "}{t134}, Address:{" "}{t135}</li>;
        $[384] = t132;
        $[385] = t134;
        $[386] = t135;
        $[387] = t14;
      } else {
        t14 = $[387];
      }
      if ($[388] !== resolvedReceiving.providerLabel || $[389] !== resolvedReceiving.providerNpi || $[390] !== resolvedReceiving.providerWriteSafe) {
        t15 = <li>Receiving provider:{" "}<ResolvedProviderCopyFields providerLabel={resolvedReceiving.providerLabel} providerNpi={resolvedReceiving.providerNpi} writeSafe={resolvedReceiving.providerWriteSafe} /></li>;
        $[388] = resolvedReceiving.providerLabel;
        $[389] = resolvedReceiving.providerNpi;
        $[390] = resolvedReceiving.providerWriteSafe;
        $[391] = t15;
      } else {
        t15 = $[391];
      }
      t10 = "Patient Type: ";
      if ($[392] !== patientLabel) {
        t11 = <CopyableContent text={patientLabel} />;
        $[392] = patientLabel;
        $[393] = t11;
      } else {
        t11 = $[393];
      }
      t8 = "ml-4 mt-1 space-y-1 list-disc list-inside text-xs";
      t9 = sortedPastAppointments.length === 0 ? <li>No appointment history found</li> : sortedPastAppointments.map(_temp38);
    }
    $[15] = actionNote;
    $[16] = athenaData?.coverages;
    $[17] = athenaData?.patientDetails;
    $[18] = bucketLabel;
    $[19] = chartReferralAuths;
    $[20] = chartRequestLetters;
    $[21] = coverageReadback;
    $[22] = coverageSlots.primary;
    $[23] = coverageSlots.secondary;
    $[24] = dateReceived;
    $[25] = decisionTrace;
    $[26] = documentId;
    $[27] = documentUploadLabels;
    $[28] = eligibilityDisplay;
    $[29] = emergencyContactName;
    $[30] = emergencyContactPhoneNumber;
    $[31] = emergencyContactRelationship;
    $[32] = followThroughEligible;
    $[33] = insuranceRequirements;
    $[34] = internalNoteToEnter;
    $[35] = isPrimaryCareProvider;
    $[36] = pastAppointments;
    $[37] = patient;
    $[38] = patientEhrId;
    $[39] = patientLabel;
    $[40] = pendingApproval;
    $[41] = primaryCareProviderAlreadyPresent;
    $[42] = primaryCoverageSummary;
    $[43] = privacyNoticeOnFile;
    $[44] = receivingAssignmentConfirmed;
    $[45] = receivingProviderName;
    $[46] = referral;
    $[47] = referralAuthorization;
    $[48] = referralSourceOnFile;
    $[49] = referringLocationAddress;
    $[50] = referringProviderMarkedPcp;
    $[51] = referringProviderName;
    $[52] = referringProviderSpecialty;
    $[53] = resolvedReceiving;
    $[54] = secondaryCoverageSummary;
    $[55] = task;
    $[56] = taskSteps;
    $[57] = unresolvedUpcomingProviders;
    $[58] = upcomingAppointmentProviderSummary;
    $[59] = upcomingAppointments;
    $[60] = urgency;
    $[61] = view;
    $[62] = T0;
    $[63] = dataSourceEntries;
    $[64] = hasMissingInformation;
    $[65] = missingFields;
    $[66] = t10;
    $[67] = t11;
    $[68] = t12;
    $[69] = t13;
    $[70] = t14;
    $[71] = t15;
    $[72] = t16;
    $[73] = t17;
    $[74] = t18;
    $[75] = t19;
    $[76] = t20;
    $[77] = t21;
    $[78] = t22;
    $[79] = t23;
    $[80] = t24;
    $[81] = t25;
    $[82] = t26;
    $[83] = t8;
    $[84] = t9;
  } else {
    T0 = $[62];
    dataSourceEntries = $[63];
    hasMissingInformation = $[64];
    missingFields = $[65];
    t10 = $[66];
    t11 = $[67];
    t12 = $[68];
    t13 = $[69];
    t14 = $[70];
    t15 = $[71];
    t16 = $[72];
    t17 = $[73];
    t18 = $[74];
    t19 = $[75];
    t20 = $[76];
    t21 = $[77];
    t22 = $[78];
    t23 = $[79];
    t24 = $[80];
    t25 = $[81];
    t26 = $[82];
    t8 = $[83];
    t9 = $[84];
  }
  if (t26 !== Symbol.for("react.early_return_sentinel")) {
    return t26;
  }
  let t27;
  if ($[394] !== t8 || $[395] !== t9) {
    t27 = <ul className={t8}>{t9}</ul>;
    $[394] = t8;
    $[395] = t9;
    $[396] = t27;
  } else {
    t27 = $[396];
  }
  let t28;
  if ($[397] !== t10 || $[398] !== t11 || $[399] !== t27) {
    t28 = <li>{t10}{t11}{t27}</li>;
    $[397] = t10;
    $[398] = t11;
    $[399] = t27;
    $[400] = t28;
  } else {
    t28 = $[400];
  }
  let t29;
  if ($[401] !== dateReceived) {
    t29 = <li>Date: <CopyableContent text={dateReceived} /></li>;
    $[401] = dateReceived;
    $[402] = t29;
  } else {
    t29 = $[402];
  }
  let t30;
  if ($[403] !== internalNoteToEnter) {
    t30 = <CopyableContent text={internalNoteToEnter} />;
    $[403] = internalNoteToEnter;
    $[404] = t30;
  } else {
    t30 = $[404];
  }
  let t31;
  if ($[405] !== flags) {
    t31 = flags.length > 0 && <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{flags.map(_temp39)}</ul>;
    $[405] = flags;
    $[406] = t31;
  } else {
    t31 = $[406];
  }
  let t32;
  if ($[407] !== t30 || $[408] !== t31) {
    t32 = <li>Internal Note: {t30}{t31}</li>;
    $[407] = t30;
    $[408] = t31;
    $[409] = t32;
  } else {
    t32 = $[409];
  }
  let t33;
  if ($[410] !== schedulingPriority) {
    t33 = <li>Urgency: <CopyableContent text={schedulingPriority} /></li>;
    $[410] = schedulingPriority;
    $[411] = t33;
  } else {
    t33 = $[411];
  }
  let t34;
  if ($[412] !== bucketLabel) {
    t34 = bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <>{NO_CONFIDENT_BUCKET_SUGGESTION}</> : <>Suggested Bucket: <CopyableContent text={SUGGESTED_BUCKET_LABELS[bucketLabel] ?? bucketLabel} /></>;
    $[412] = bucketLabel;
    $[413] = t34;
  } else {
    t34 = $[413];
  }
  let t35;
  if ($[414] !== bucketLabel || $[415] !== decisionTrace) {
    t35 = decisionTrace.length > 0 && (bucketLabel === TPR_MANUAL_REVIEW_BUCKET ? <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs">{decisionTrace.map(_temp40)}</ul> : <ul className="ml-4 mt-1 space-y-1 list-disc list-inside text-xs"><li>{decisionTrace[decisionTrace.length - 1].label}{" "}<strong>{decisionTrace[decisionTrace.length - 1].outcome}</strong></li></ul>);
    $[414] = bucketLabel;
    $[415] = decisionTrace;
    $[416] = t35;
  } else {
    t35 = $[416];
  }
  let t36;
  if ($[417] !== t34 || $[418] !== t35) {
    t36 = <li>{t34}{t35}</li>;
    $[417] = t34;
    $[418] = t35;
    $[419] = t36;
  } else {
    t36 = $[419];
  }
  let t37;
  if ($[420] !== actionNote) {
    t37 = <li>Action note: <CopyableContent text={actionNote} /></li>;
    $[420] = actionNote;
    $[421] = t37;
  } else {
    t37 = $[421];
  }
  let t38;
  if ($[422] !== internalNoteToEnter) {
    t38 = <li>Go to quickview and add the internal note: <CopyableContent text={internalNoteToEnter} /></li>;
    $[422] = internalNoteToEnter;
    $[423] = t38;
  } else {
    t38 = $[423];
  }
  let t39;
  if ($[424] !== t12 || $[425] !== t13 || $[426] !== t14 || $[427] !== t15 || $[428] !== t28 || $[429] !== t29 || $[430] !== t32 || $[431] !== t33 || $[432] !== t36 || $[433] !== t37 || $[434] !== t38) {
    t39 = <ol className={t12}>{t13}{t14}{t15}{t28}{t29}{t32}{t33}{t36}{t37}{t38}</ol>;
    $[424] = t12;
    $[425] = t13;
    $[426] = t14;
    $[427] = t15;
    $[428] = t28;
    $[429] = t29;
    $[430] = t32;
    $[431] = t33;
    $[432] = t36;
    $[433] = t37;
    $[434] = t38;
    $[435] = t39;
  } else {
    t39 = $[435];
  }
  let t40;
  if ($[436] !== t16 || $[437] !== t39) {
    t40 = <div>{t16}{t39}</div>;
    $[436] = t16;
    $[437] = t39;
    $[438] = t40;
  } else {
    t40 = $[438];
  }
  let t41;
  let t42;
  if ($[439] === Symbol.for("react.memo_cache_sentinel")) {
    t41 = <h4 className="font-semibold text-foreground mb-2">Doc Upload</h4>;
    t42 = <p className="mb-1">Verify the following documents were uploaded:</p>;
    $[439] = t41;
    $[440] = t42;
  } else {
    t41 = $[439];
    t42 = $[440];
  }
  let t43;
  if ($[441] !== documentUploadLabels) {
    t43 = documentUploadLabels.length === 0 ? <li>N/A</li> : documentUploadLabels.map(_temp41);
    $[441] = documentUploadLabels;
    $[442] = t43;
  } else {
    t43 = $[442];
  }
  let t44;
  if ($[443] !== t43) {
    t44 = <div>{t41}{t42}<ul className="ml-4 space-y-1 list-disc list-inside">{t43}</ul></div>;
    $[443] = t43;
    $[444] = t44;
  } else {
    t44 = $[444];
  }
  let t45;
  let t46;
  if ($[445] === Symbol.for("react.memo_cache_sentinel")) {
    t45 = <h4 className="font-semibold text-foreground mb-2">Missing Information</h4>;
    t46 = <p className="font-medium text-foreground">Step 1: Missing Information Check</p>;
    $[445] = t45;
    $[446] = t46;
  } else {
    t45 = $[445];
    t46 = $[446];
  }
  let t47;
  if ($[447] !== hasMissingInformation || $[448] !== missingFields) {
    t47 = hasMissingInformation ? `Missing: ${missingFields.map(_temp42).join(", ")}` : "No missing-information letter needed.";
    $[447] = hasMissingInformation;
    $[448] = missingFields;
    $[449] = t47;
  } else {
    t47 = $[449];
  }
  let t48;
  if ($[450] !== t47) {
    t48 = <p className="mb-3">{t47}</p>;
    $[450] = t47;
    $[451] = t48;
  } else {
    t48 = $[451];
  }
  let t49;
  if ($[452] === Symbol.for("react.memo_cache_sentinel")) {
    t49 = <p className="font-medium text-foreground">Step 2: Data Source Checklist</p>;
    $[452] = t49;
  } else {
    t49 = $[452];
  }
  let t50;
  if ($[453] !== dataSourceEntries) {
    t50 = <ul className="ml-4 mb-3 space-y-1 list-disc list-inside">{dataSourceEntries.map(_temp43)}</ul>;
    $[453] = dataSourceEntries;
    $[454] = t50;
  } else {
    t50 = $[454];
  }
  let t51;
  if ($[455] !== dateReceived || $[456] !== hasMissingInformation || $[457] !== referringProviderName) {
    t51 = hasMissingInformation && <ol className="list-decimal list-inside space-y-2"><li>Verify the Auto-Generated document was added to the patient chart</li><li>Send the document out.<ul className="ml-4 mt-1 space-y-2 list-none"><li>a. Chart Export - sending to referring provider<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Click the hamburger menu (3 dashes) → select Chart Export.</li><li>Click the + button next to Admin Documents.</li><li>Find the "Request for Additional Information" doc and move it to the right-hand side.</li><li>Click Export Chart Sections → send to <CopyableContent text={referringProviderName} /></li></ul></li><li>c. Fill out export details<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>Type <CopyableContent text={referringProviderName} /></li><li>Confirm method is set to Athena Fax and the fax number has populated.</li><li>Attention box: <CopyableContent text={referringProviderName} /></li><li>Note field: "Request for missing information."</li><li>Confirm Submit, then Save.</li></ul></li><li>d. Add Note to Patient Chart<ul className="ml-4 mt-1 space-y-1 list-disc list-inside"><li>"Valerie Health: Missing Information Requested <CopyableContent text={dateReceived} />"</li></ul></li></ul></li></ol>;
    $[455] = dateReceived;
    $[456] = hasMissingInformation;
    $[457] = referringProviderName;
    $[458] = t51;
  } else {
    t51 = $[458];
  }
  let t52;
  if ($[459] !== t48 || $[460] !== t50 || $[461] !== t51) {
    t52 = <div>{t45}{t46}{t48}{t49}{t50}{t51}</div>;
    $[459] = t48;
    $[460] = t50;
    $[461] = t51;
    $[462] = t52;
  } else {
    t52 = $[462];
  }
  let t53;
  if ($[463] !== t17 || $[464] !== t18 || $[465] !== t19 || $[466] !== t20 || $[467] !== t21 || $[468] !== t22 || $[469] !== t23 || $[470] !== t40 || $[471] !== t44 || $[472] !== t52) {
    t53 = <div className={t17}>{t18}{t19}{t20}{t21}{t22}{t23}{t40}{t44}{t52}</div>;
    $[463] = t17;
    $[464] = t18;
    $[465] = t19;
    $[466] = t20;
    $[467] = t21;
    $[468] = t22;
    $[469] = t23;
    $[470] = t40;
    $[471] = t44;
    $[472] = t52;
    $[473] = t53;
  } else {
    t53 = $[473];
  }
  let t54;
  if ($[474] !== T0 || $[475] !== t24 || $[476] !== t53) {
    t54 = <T0 collapsibleTitle={t24}>{t53}</T0>;
    $[474] = T0;
    $[475] = t24;
    $[476] = t53;
    $[477] = t54;
  } else {
    t54 = $[477];
  }
  let t55;
  if ($[478] !== referral.id || $[479] !== task.id) {
    t55 = <ReferralEligibilityCard referralId={referral.id} taskId={task.id} />;
    $[478] = referral.id;
    $[479] = task.id;
    $[480] = t55;
  } else {
    t55 = $[480];
  }
  let t56;
  if ($[481] !== athenaData || $[482] !== referral) {
    t56 = <AthenaReferralCards referral={referral} athenaData={athenaData} />;
    $[481] = athenaData;
    $[482] = referral;
    $[483] = t56;
  } else {
    t56 = $[483];
  }
  let t57;
  if ($[484] !== t25 || $[485] !== t54 || $[486] !== t55 || $[487] !== t56) {
    t57 = <div className={t25}>{t54}{t55}{t56}</div>;
    $[484] = t25;
    $[485] = t54;
    $[486] = t55;
    $[487] = t56;
    $[488] = t57;
  } else {
    t57 = $[488];
  }
  return t57;
}
function _temp43(entry_2) {
  return <li key={entry_2.label}>{entry_2.label}: <CopyableContent text={entry_2.value} /> ({entry_2.source})</li>;
}
function _temp42(entry_1) {
  return entry_1.label;
}
function _temp41(label) {
  return <li key={label}><CopyableContent text={label} /></li>;
}
function _temp40(entry_0, index_3) {
  return <li key={`${entry_0.label}-${index_3}`}>{entry_0.label} <strong>{entry_0.outcome}</strong></li>;
}
function _temp39(flag_0, index_2) {
  return <li key={index_2}>{flag_0}</li>;
}
function _temp38(appointment) {
  return <li key={appointment.id}>{formatAppointmentHistoryEntry(appointment)}</li>;
}
function _temp37(entry, index_1) {
  return <li key={index_1}>{entry}</li>;
}
function _temp36(flag, index_0) {
  return <li key={index_0}>{flag}</li>;
}
function _temp35(coverage_0) {
  return coverage_0.order === 2;
}
function _temp34(coverage) {
  return coverage.order === 1;
}
function _temp33(a_0, b_0) {
  return (a_0.order ?? 99) - (b_0.order ?? 99);
}
function _temp32(value_0, index, values) {
  return values.indexOf(value_0) === index;
}
function _temp31(value) {
  return Boolean(value);
}
function _temp30(item) {
  return item.diagnosisText;
}
function _temp29(a, b) {
  return (a.order ?? 99) - (b.order ?? 99);
}